import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { HfPlayerRef as PlayerRef } from "../hyperframesPlayer";
import {
  api,
  ALL_STATUSES,
  STATUS_META,
  type Idea,
  type OverlayLayer,
  type ReelPreset,
  type ReelTemplate,
  type ReelScene,
  type SceneMeta,
  type TextOverlayLayer,
  type WatermarkConfig,
  type WatermarkPosition,
  type MusicConfig,
  type VoiceoverConfig,
  type AudioAsset,
  type SfxEvent,
  type SfxName,
  type SfxConfig,
  type WorkspaceReel,
  type WorkspaceStatus,
} from "../api";
import { resolveSceneWatermark } from "../lib/sceneWatermark";
import { buildSceneSfxRecipe, hasDedicatedSceneRecipe } from "../lib/sceneSfxRecipes";
import { FPS, resolveClipLayout } from "../templates";
import { calcReelDurationFrames } from "../lib/hyperframesCompositions";
import {
  ensureSegments as voEnsureSegments,
  newSegmentId as voNewSegmentId,
  patchSegment as voPatchSegment,
  removeSegment as voRemoveSegment,
  splitAtPlayhead as voSplitAtPlayhead,
} from "../lib/voiceover";
import { decodeBlob, encodeWav, encodeMp3, downloadBlob } from "../lib/audioEncoders";
import type { StopResult } from "../lib/voiceoverRecorder";
import { SceneLibrary } from "./composer/SceneLibrary";
import { ElementLibrary, findElementById } from "./composer/ElementLibrary";
import type { ElementTemplate } from "./composer/elementCatalog";
import { Timeline } from "./composer/Timeline";
import { Inspector } from "./composer/Inspector";
import { OverlayInspector } from "./composer/OverlayInspector";
import { LayersPanel } from "./composer/LayersPanel";
import { ReelPlayerPreview, type ReelPlayerHandle } from "./composer/ReelPlayerPreview";
import { AiPromptModal } from "./composer/AiPromptModal";
import { PartnerProductsImportModal } from "../components/PartnerProductsImportModal";
import { QuickAddBar } from "./composer/QuickAddBar";
import { NotesPanel } from "./composer/NotesPanel";
import { ChangeRequestsPanel } from "./composer/ChangeRequestsPanel";
import { WatermarkPanel } from "./composer/WatermarkPanel";
import { MusicPanel } from "./composer/MusicPanel";
import { VoiceoverPanel } from "./composer/VoiceoverPanel";
import { ElevenLabsPanel } from "./composer/ElevenLabsPanel";
import { SfxGallery, SfxInspector } from "./composer/SfxGallery";
import { TransportBar } from "./composer/TransportBar";
import { Tooltip } from "./composer/Tooltip";
import { DockablePanel } from "./composer/DockablePanel";
import { ColumnResizer } from "./composer/ColumnResizer";
import { useDockLayout } from "./composer/useDockLayout";
import {
  IconButton,
  Popover,
  SegmentedControl,
  Toolbar,
  ToolbarSep,
  ToolbarSpacer,
  MenuItem,
  MenuSection,
  MenuDivider,
} from "./composer/ui/Primitives";

const DEFAULT_TARGET_SEC = 15;

const DEFAULT_WATERMARK: WatermarkConfig = {
  enabled: true,
  position: "br",
  opacity: 0.78,
  size: 60,
  paddingPx: 28,
  // Watermark-Settings sind seit dem Per-Scene-Refactor INDIVIDUELL pro
  // Szene konfigurierbar (siehe `inputs.extras.wm*`). `showOnAllScenes`
  // gibt es im Datenmodell weiterhin (Backward-Compat fürs Workspace-File);
  // die UI nutzt es nicht mehr — Sichtbarkeit pro Szene steuert
  // `extras.hideLogo`, alles andere erbt aus diesen globalen Defaults.
  showOnAllScenes: false,
};

type Props = {
  formats: string[];
  locales: string[];
  initialIdea: Idea | null;
  initialPreset: ReelPreset | null;
  /** When set, hydrate composer from a complete template (scenes + overlays + format). */
  initialTemplate?: ReelTemplate | null;
  /** When set, hydrate composer from a saved workspace reel id. */
  initialWorkspaceId?: string | null;
  onQueued: (msg: string, opensQueue: boolean) => void;
  /** Called after a successful Save so the parent can show toasts. */
  onSaved?: (reel: WorkspaceReel) => void;
};

let __uid = 0;
const newUid = (): string => `s${Date.now()}_${++__uid}`;

const cloneScenes = (scenes: ReelScene[]): ReelScene[] =>
  scenes.map((s) => ({
    ...s,
    uid: newUid(),
    // Backfill naturalDurationSec so legacy reels keep their authored look:
    // their clip width remains the natural pace until the user opts in to
    // trimming (resize without ⌥) or stretching (resize with ⌥).
    naturalDurationSec: s.naturalDurationSec ?? s.durationSec,
    inputs: { ...s.inputs, extras: { ...(s.inputs.extras ?? {}) } },
  }));

type SidebarTab =
  | "layers"
  | "inspector"
  | "notes"
  | "cr"
  | "watermark"
  | "music"
  | "voiceover"
  | "sfx"
  | "eleven";

export const Composer: React.FC<Props> = ({
  formats,
  locales,
  initialIdea,
  initialPreset,
  initialTemplate,
  initialWorkspaceId,
  onQueued,
  onSaved,
}) => {
  const [scenesCatalog, setScenesCatalog] = useState<SceneMeta[]>([]);
  const [scenes, setScenes] = useState<ReelScene[]>([]);
  /** Free-form text/sticker layers drawn over the scene composition. */
  const [overlays, setOverlays] = useState<OverlayLayer[]>([]);
  /** Unabhängige SFX-Events auf der Timeline (clip-unabhängig). */
  const [sfxEvents, setSfxEvents] = useState<SfxEvent[]>([]);
  const [title, setTitle] = useState<string>(
    initialTemplate?.title ?? initialPreset?.title ?? initialIdea?.title ?? "Custom Reel",
  );
  const [selectedUidRaw, setSelectedUidRaw] = useState<string | null>(null);
  /**
   * Photoshop-style multi-selection. Held alongside the single "primary"
   * uid so existing inspector / player code keeps working unchanged while
   * the LayersPanel can additionally bulk-edit a wider set.
   *
   * Invariant: when the set is non-empty, `selectedUidRaw` is one of its
   * members (the most-recently-clicked).
   */
  const [selectedUids, setSelectedUids] = useState<Set<string>>(new Set());

  /**
   * Drop-in replacement for `setSelectedUid` that also collapses the multi-set
   * to the new single primary. All "set-the-active-thing" call sites use this.
   */
  const setSelectedUid = useCallback((uid: string | null) => {
    setSelectedUidRaw(uid);
    setSelectedUids(uid ? new Set([uid]) : new Set());
  }, []);
  const selectedUid = selectedUidRaw;

  /**
   * Multi-select entry-point used by the LayersPanel.
   * - "replace": clears the set, selects only `uid`
   * - "toggle":  Cmd/Ctrl-click — toggles `uid` membership; primary follows
   * - "range":   Shift-click — selects everything between current primary
   *              and `uid` (inclusive), using `orderedUids` as the
   *              authoritative top-to-bottom ordering of pickable rows
   */
  const applySelection = useCallback(
    (
      uid: string | null,
      mode: "replace" | "toggle" | "range",
      orderedUids?: string[],
    ) => {
      if (uid == null) {
        setSelectedUidRaw(null);
        setSelectedUids(new Set());
        return;
      }
      if (mode === "replace") {
        setSelectedUidRaw(uid);
        setSelectedUids(new Set([uid]));
        return;
      }
      setSelectedUids((prev) => {
        const next = new Set(prev);
        if (mode === "toggle") {
          if (next.has(uid)) {
            next.delete(uid);
            const remaining = Array.from(next);
            setSelectedUidRaw(remaining[remaining.length - 1] ?? null);
          } else {
            next.add(uid);
            setSelectedUidRaw(uid);
          }
          return next;
        }
        // mode === "range"
        if (orderedUids && orderedUids.length > 0) {
          const anchor = selectedUidRaw ?? uid;
          const i1 = orderedUids.indexOf(anchor);
          const i2 = orderedUids.indexOf(uid);
          if (i1 !== -1 && i2 !== -1) {
            const [lo, hi] = i1 < i2 ? [i1, i2] : [i2, i1];
            for (let i = lo; i <= hi; i += 1) next.add(orderedUids[i]);
            setSelectedUidRaw(uid);
            return next;
          }
        }
        next.add(uid);
        setSelectedUidRaw(uid);
        return next;
      });
    },
    [selectedUidRaw],
  );
  const [previewFormat, setPreviewFormat] = useState<string>("9:16");
  const [chosenFormats, setChosenFormats] = useState<string[]>(["9:16"]);
  const [chosenLocales, setChosenLocales] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [productsImportOpen, setProductsImportOpen] = useState(false);
  /**
   * Reel-weiter SFX-Master (Volume + Mute) — wird persistent im Workspace
   * gespeichert. Default: 100 % laut, nicht stumm.
   *
   * Wichtig: Beim Mute ändern wir AUSSCHLIESSLICH dieses Objekt — wir
   * patchen NICHT mehr `extras.sfx` auf jeder Szene und ersetzen die
   * `sfxEvents`-Liste auch nicht durch `[]`. Sonst bekommen die Player-
   * inputProps eine neue Identität und die `<Audio>`-Tags der Szenen-
   * Vorlagen-SFX werden ent-/wieder-gemountet — dadurch hat die Musik
   * vorher gestottert.
   */
  const [sfxConfig, setSfxConfig] = useState<SfxConfig>({ volume: 1, muted: false });
  const [safeZoneOn, setSafeZoneOn] = useState(false);
  const [safeZonePlatform, setSafeZonePlatform] = useState<"general" | "tiktok" | "reels" | "shorts" | "stories">("reels");
  // Render-Theme des Reels ("dark" | "light"). Wirkt reel-weit auf ALLE Szenen
  // (Palette bg/fg/muted); Akzente bleiben. Live-Vorschau aktualisiert sofort.
  const [theme, setTheme] = useState<"dark" | "light">("dark");

  // ---------- Workspace state ----------
  const [workspaceId, setWorkspaceId] = useState<string | null>(initialWorkspaceId ?? null);
  const [status, setStatus] = useState<WorkspaceStatus>("draft");
  const [watermark, setWatermark] = useState<WatermarkConfig>(DEFAULT_WATERMARK);
  const [music, setMusic] = useState<MusicConfig | undefined>(undefined);
  /** Voiceover-Spur — eine pro Reel, vom User hochgeladene Sprachaufnahme. */
  const [voiceover, setVoiceover] = useState<VoiceoverConfig | undefined>(undefined);
  const [notes, setNotes] = useState<WorkspaceReel["notes"]>([]);
  const [changeRequests, setChangeRequests] = useState<WorkspaceReel["changeRequests"]>([]);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [sidebarTab, setSidebarTab] = useState<SidebarTab>("inspector");
  /** Left sidebar (library) — tab between scene catalog and element library. */
  const [libraryTab, setLibraryTab] = useState<"scenes" | "elements">("scenes");

  /** Dock layout — Spaltenbreiten, Collapse-/Popup-Modi, Persistenz. */
  const dock = useDockLayout();
  const isMobile = dock.viewport === "mobile";
  // Auf Tablets darf der User die Panels noch andocken, aber Default = collapsed
  // (verhindert Quetschen). Auf Mobile = immer collapsed bis User es explizit öffnet.
  const libMode = isMobile && dock.state.modes.library === "docked" ? "collapsed" : dock.state.modes.library;
  const insMode = isMobile && dock.state.modes.inspector === "docked" ? "collapsed" : dock.state.modes.inspector;
  const tlMode = dock.state.modes.timeline;

  const [playheadFrame, setPlayheadFrame] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState<"idle" | "pending" | "saving" | "saved" | "error">("idle");
  const playerHandleRef = useRef<ReelPlayerHandle | null>(null);
  // Hold the live PlayerRef in state (not a ref) so listener-attaching
  // useEffects re-run reliably whenever the underlying Player instance
  // remounts. This kills the "black screen / play button does nothing" bug.
  const [player, setPlayer] = useState<PlayerRef | null>(null);

  // ---------- Global TransportBar state ----------
  const [locked, setLocked] = useState(false);
  const [snap, setSnap] = useState(true);
  const [loopOn, setLoopOn] = useState(true);
  const [muted, setMuted] = useState(false);
  const [speed, setSpeed] = useState(1);
  /** Timeline-Zoom (1 = passt komplett rein, 2 = doppelt so breit, …).
   *  Wird in localStorage persistiert, damit der User nicht bei jedem Reload
   *  zurückgesetzt wird. */
  const [timelineZoom, setTimelineZoom] = useState<number>(() => {
    if (typeof window === "undefined") return 1;
    const raw = window.localStorage.getItem("allyver.timeline.zoom");
    const n = raw ? Number(raw) : 1;
    return Number.isFinite(n) && n > 0 ? Math.min(12, Math.max(0.4, n)) : 1;
  });
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem("allyver.timeline.zoom", String(timelineZoom));
  }, [timelineZoom]);
  /** Audio-Lane-Höhen-Multiplikator (Music/VO/SFX). 1 = default. */
  const [audioRowScale, setAudioRowScale] = useState<number>(() => {
    if (typeof window === "undefined") return 1;
    const raw = window.localStorage.getItem("allyver.timeline.audioScale");
    const n = raw ? Number(raw) : 1;
    return Number.isFinite(n) && n > 0 ? Math.min(3, Math.max(0.5, n)) : 1;
  });
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem("allyver.timeline.audioScale", String(audioRowScale));
  }, [audioRowScale]);

  /** Default-State für „Vorschau während der Mic-Aufnahme abspielen". Default
   *  true (Lip-Sync), aber persistiert. */
  const [recordPlayPreview, setRecordPlayPreview] = useState<boolean>(() => {
    if (typeof window === "undefined") return true;
    return window.localStorage.getItem("allyver.recorder.autoplay") !== "0";
  });
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem("allyver.recorder.autoplay", recordPlayPreview ? "1" : "0");
  }, [recordPlayPreview]);
  /** Bumped when the user hits "Reset Player" — forces a clean Player remount. */
  const [remountKey, setRemountKey] = useState(0);
  const stageRef = useRef<HTMLElement | null>(null);

  // ---------- Help mode (rich tooltips fire instantly + show ?-pulses) ----------
  const [helpMode, setHelpMode] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem("allyver.helpMode") === "1";
  });
  useEffect(() => {
    (window as unknown as { __allyverHelpMode__?: boolean }).__allyverHelpMode__ = helpMode;
    document.body.classList.toggle("help-mode-on", helpMode);
    window.localStorage.setItem("allyver.helpMode", helpMode ? "1" : "0");
  }, [helpMode]);

  /**
   * Globaler SFX-Mute-Toggle (Toolbar + Timeline-„🔇"-Button). Schaltet
   * NUR `sfxConfig.muted` um — die Szenen-Inputs und die `sfxEvents`-Liste
   * bleiben unangetastet, damit der Player nicht remountet und die Musik
   * sauber durchläuft.
   */
  const toggleGlobalSfx = useCallback((): void => {
    setSfxConfig((cur) => ({ ...cur, muted: !cur.muted }));
  }, []);

  /** Master-Lautstärke für alle SFX (0..1). 0 entspricht Stumm. */
  const setSfxVolume = useCallback((volume: number): void => {
    const clamped = Math.max(0, Math.min(1, volume));
    setSfxConfig((cur) => ({ ...cur, volume: clamped }));
  }, []);

  // Mark dirty whenever editable state changes after first hydration.
  const hydrating = useRef(true);
  useEffect(() => {
    if (hydrating.current) {
      hydrating.current = false;
      return;
    }
    setDirty(true);
  }, [scenes, overlays, sfxEvents, sfxConfig, title, watermark, music, voiceover, status]);

  // ---------- UNDO / REDO ----------
  // Snapshots of the editable state. We coalesce rapid edits (typing, dragging)
  // into a single undo step using a debounce: the snapshot at the START of
  // each edit-burst is what the user reverts to. After the burst quiets down
  // (~400ms), the new "stable" state becomes the next baseline.
  type EditSnapshot = {
    scenes: ReelScene[];
    overlays: OverlayLayer[];
    sfxEvents: SfxEvent[];
    sfxConfig: SfxConfig;
    title: string;
    watermark: WatermarkConfig;
    music: MusicConfig | undefined;
    voiceover: VoiceoverConfig | undefined;
  };
  const HISTORY_LIMIT = 80;
  const HISTORY_DEBOUNCE_MS = 400;
  const historyRef = useRef<{
    past: EditSnapshot[];
    future: EditSnapshot[];
    /** Last "stable" state — the rollback target for the next burst. */
    committed: EditSnapshot | null;
    timer: number | null;
  }>({ past: [], future: [], committed: null, timer: null });
  const applyingHistoryRef = useRef(false);
  const currentRef = useRef<EditSnapshot | null>(null);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  useEffect(() => {
    currentRef.current = {
      scenes,
      overlays,
      sfxEvents,
      sfxConfig,
      title,
      watermark,
      music,
      voiceover,
    };
  });

  useEffect(() => {
    // Undo/redo dispatched a setState burst — don't re-record it as new history.
    if (applyingHistoryRef.current) {
      applyingHistoryRef.current = false;
      historyRef.current.committed = currentRef.current;
      return;
    }
    if (historyRef.current.committed === null) {
      historyRef.current.committed = currentRef.current;
      return;
    }
    if (historyRef.current.timer === null) {
      // First change of a new burst — push the pre-burst stable state.
      const m = historyRef.current.committed;
      if (m) {
        historyRef.current.past.push(m);
        if (historyRef.current.past.length > HISTORY_LIMIT) historyRef.current.past.shift();
        historyRef.current.future = [];
        setCanUndo(true);
        setCanRedo(false);
      }
    } else {
      window.clearTimeout(historyRef.current.timer);
    }
    historyRef.current.timer = window.setTimeout(() => {
      historyRef.current.committed = currentRef.current;
      historyRef.current.timer = null;
    }, HISTORY_DEBOUNCE_MS);
  }, [scenes, overlays, sfxEvents, sfxConfig, title, watermark, music, voiceover]);

  const applyEditSnapshot = useCallback((snap: EditSnapshot): void => {
    applyingHistoryRef.current = true;
    setScenes(snap.scenes);
    setOverlays(snap.overlays);
    setSfxEvents(snap.sfxEvents);
    setSfxConfig(snap.sfxConfig);
    setTitle(snap.title);
    setWatermark(snap.watermark);
    setMusic(snap.music);
    setVoiceover(snap.voiceover);
  }, []);

  const undo = useCallback((): void => {
    if (historyRef.current.past.length === 0) return;
    if (historyRef.current.timer) {
      window.clearTimeout(historyRef.current.timer);
      historyRef.current.timer = null;
    }
    const cur = currentRef.current;
    const prev = historyRef.current.past.pop();
    if (!prev) return;
    if (cur) historyRef.current.future.push(cur);
    applyEditSnapshot(prev);
    setCanUndo(historyRef.current.past.length > 0);
    setCanRedo(true);
  }, [applyEditSnapshot]);

  const redo = useCallback((): void => {
    if (historyRef.current.future.length === 0) return;
    if (historyRef.current.timer) {
      window.clearTimeout(historyRef.current.timer);
      historyRef.current.timer = null;
    }
    const cur = currentRef.current;
    const next = historyRef.current.future.pop();
    if (!next) return;
    if (cur) historyRef.current.past.push(cur);
    applyEditSnapshot(next);
    setCanUndo(true);
    setCanRedo(historyRef.current.future.length > 0);
  }, [applyEditSnapshot]);

  // Cmd/Ctrl+Z = Undo, Cmd/Ctrl+Shift+Z (or Cmd+Y) = Redo. Skip when an
  // editable input is focused so native browser undo (text caret) still works.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      const t = e.target as HTMLElement | null;
      const inEditable =
        !!t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable);
      if (inEditable) return;
      const meta = e.metaKey || e.ctrlKey;
      if (!meta) return;
      const k = e.key.toLowerCase();
      if (k === "z" && !e.shiftKey) {
        e.preventDefault();
        undo();
      } else if ((k === "z" && e.shiftKey) || k === "y") {
        e.preventDefault();
        redo();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [undo, redo]);

  useEffect(() => {
    void (async () => {
      try {
        const cat = await api.scenes();
        setScenesCatalog(cat);
      } catch (e) {
        setErr(`Scene-Katalog: ${(e as Error).message}`);
      }
    })();
  }, []);

  useEffect(() => {
    if (locales.length && chosenLocales.length === 0) {
      setChosenLocales([locales[0]]);
    }
  }, [locales, chosenLocales.length]);

  // Initial hydration — preset, idea, or workspace reel.
  useEffect(() => {
    void (async () => {
      hydrating.current = true;
      if (initialWorkspaceId) {
        try {
          const r = await api.workspaceGet(initialWorkspaceId);
          const fresh = cloneScenes(r.scenes);
          setScenes(fresh);
          setOverlays(r.overlays ?? []);
          setSfxEvents(r.sfxEvents ?? []);
          setSfxConfig(
            r.sfxConfig
              ? {
                  volume: Math.max(0, Math.min(1, r.sfxConfig.volume ?? 1)),
                  muted: r.sfxConfig.muted === true,
                }
              : { volume: 1, muted: false },
          );
          setSelectedUid(fresh[0]?.uid ?? null);
          setTitle(r.title);
          setStatus(r.status);
          setWatermark({ ...DEFAULT_WATERMARK, ...(r.watermark ?? {}) });
          setMusic(r.music);
          setVoiceover(r.voiceover ? voEnsureSegments(r.voiceover) : undefined);
          setNotes(r.notes);
          setChangeRequests(r.changeRequests);
          setWorkspaceId(r.id);
          setSavedAt(r.updatedAt);
          setPreviewFormat(r.format);
          setTheme(r.theme ?? "dark");
          setDirty(false);
        } catch (e) {
          setErr(`Konnte Workspace-Reel nicht laden: ${(e as Error).message}`);
        }
      } else if (initialTemplate) {
        const fresh = cloneScenes(initialTemplate.scenes);
        setScenes(fresh);
        // Re-uid overlays to avoid collisions across multiple loads.
        const overlaysFresh = initialTemplate.overlays.map((o) => ({
          ...o,
          uid: newUid(),
        })) as OverlayLayer[];
        setOverlays(overlaysFresh);
        setSfxEvents([]);
        setSelectedUid(fresh[0]?.uid ?? null);
        setTitle(initialTemplate.title);
        setPreviewFormat(initialTemplate.format);
      } else if (initialPreset) {
        const fresh = cloneScenes(initialPreset.scenes);
        setScenes(fresh);
        setOverlays([]);
        setSfxEvents([]);
        setSelectedUid(fresh[0]?.uid ?? null);
        setTitle(initialPreset.title);
      } else if (initialIdea) {
        try {
          const p = await api.reelPreset(initialIdea.id);
          const fresh = cloneScenes(p.scenes);
          setScenes(fresh);
          setOverlays([]);
          setSfxEvents([]);
          setSelectedUid(fresh[0]?.uid ?? null);
          setTitle(p.title);
        } catch {
          setScenes([]);
          setOverlays([]);
          setSfxEvents([]);
        }
      }
    })();
  }, [initialPreset, initialIdea, initialTemplate, initialWorkspaceId]);

  const sceneIndex: Record<string, SceneMeta> = useMemo(
    () => Object.fromEntries(scenesCatalog.map((s) => [s.id, s])),
    [scenesCatalog],
  );

  /** Resolved per-clip layout (start time, duration, track) — single source of truth
   *  for the Timeline UI, the Player frame count, and totalSec. Honours both the
   *  legacy sequential layout and the new free-form / multi-track layout. */
  const layout = useMemo(() => resolveClipLayout(scenes, FPS), [scenes]);

  const totalFrames = useMemo(
    () => calcReelDurationFrames(scenes, FPS, overlays, sfxEvents, music, voiceover),
    [scenes, overlays, sfxEvents, music, voiceover],
  );
  const totalSec = totalFrames / FPS;
  /** Highest used track index — used by Timeline to render N+1 lanes. */
  const maxTrack = useMemo(
    () => layout.reduce((m, c) => Math.max(m, c.track), 0),
    [layout],
  );

  /**
   * Receives the live PlayerRef from the preview. Called with `null` when the
   * Player unmounts so we can drop the reference. We deliberately do NOT
   * attach event listeners here — that's done in the useEffect below where
   * cleanup is guaranteed.
   */
  const handlePlayerReady = useCallback((p: PlayerRef | null): void => {
    setPlayer(p);
  }, []);

  // Attach all player event listeners to the CURRENT player instance and tear
  // them down when it unmounts. This is the core fix for the "black screen +
  // dead controls" bug: previously listeners were re-attached on every render,
  // so isPlaying flipped chaotically and play()/pause() seemed broken.
  useEffect(() => {
    if (!player) return;
    const onFrame = (e: { detail: { frame: number } }) => setPlayheadFrame(e.detail.frame);
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onEnded = () => {
      setIsPlaying(false);
      // When loop is on, Remotion auto-restarts. When off, park on last frame.
    };
    player.addEventListener("frameupdate", onFrame as never);
    player.addEventListener("play", onPlay as never);
    player.addEventListener("pause", onPause as never);
    player.addEventListener("ended", onEnded as never);
    // Sync transport state to the freshly-mounted player.
    try {
      setIsPlaying(player.isPlaying());
      setPlayheadFrame(player.getCurrentFrame());
      player.setPlaybackRate(speed);
      if (muted) player.mute();
      else player.unmute();
    } catch {
      /* methods not available pre-mount; safe to skip */
    }
    return () => {
      player.removeEventListener("frameupdate", onFrame as never);
      player.removeEventListener("play", onPlay as never);
      player.removeEventListener("pause", onPause as never);
      player.removeEventListener("ended", onEnded as never);
    };
    // We intentionally don't re-run on speed/muted changes — those are pushed
    // down in dedicated effects below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [player]);

  // Push speed → player whenever it changes (or the player remounts).
  useEffect(() => {
    if (!player) return;
    try {
      player.setPlaybackRate(speed);
    } catch {
      /* ignore */
    }
  }, [player, speed]);

  // Push mute → player whenever it changes (or the player remounts).
  useEffect(() => {
    if (!player) return;
    try {
      if (muted) player.mute();
      else player.unmute();
    } catch {
      /* ignore */
    }
  }, [player, muted]);

  const onSeekSec = useCallback(
    (sec: number): void => {
      if (!player) return;
      const frame = Math.max(0, Math.round(sec * FPS));
      player.seekTo(frame);
      setPlayheadFrame(frame);
    },
    [player],
  );

  /** Hard reset — bumps remountKey to recreate the Player from scratch. Use this
   *  when the preview gets stuck on a black frame (autoplay-policy edge case). */
  const resetPlayer = useCallback(() => {
    setRemountKey((k) => k + 1);
    setIsPlaying(false);
    setPlayheadFrame(0);
  }, []);

  /**
   * Pause-on-drag. Without this, every pointer-move during a watermark/scene
   * drag mutates inputProps → the Player tries to re-render its entire
   * composition tree on the next frame WHILE simultaneously decoding the
   * playback frame → visible stutter, tearing, and occasional black flashes.
   * We pause before the first move and remember the previous play state so
   * we can resume on release.
   */
  const wasPlayingBeforeDrag = useRef(false);
  const handleDragStart = useCallback(() => {
    if (!player) return;
    try {
      wasPlayingBeforeDrag.current = player.isPlaying();
      if (wasPlayingBeforeDrag.current) player.pause();
    } catch {
      /* ignore */
    }
  }, [player]);
  const handleDragEnd = useCallback(() => {
    if (!player) return;
    try {
      if (wasPlayingBeforeDrag.current) player.play();
    } catch {
      /* ignore */
    }
    wasPlayingBeforeDrag.current = false;
  }, [player]);

  const structureSig = useMemo(
    () => `${scenes.length}|${scenes.map((s) => `${s.uid}:${s.sceneId}`).join("|")}`,
    [scenes],
  );
  useEffect(() => {
    setPlayheadFrame(0);
    setIsPlaying(false);
    // The Player auto-remounts on structure change → onPlayerReady(null) then
    // onPlayerReady(newNode) will sync state. Nothing to do here besides
    // resetting the playhead UI.
  }, [structureSig]);

  const selectedScene = scenes.find((s) => s.uid === selectedUid) ?? null;
  const selectedMeta = selectedScene ? sceneIndex[selectedScene.sceneId] ?? null : null;

  /**
   * Effective watermark of the currently-selected scene = global defaults
   * merged with `extras.wm*`. Driven into the PlayerEditOverlay so the
   * drag-handle anchors on THIS scene's logo position (not on a global
   * one, which no longer exists per-clip). When no scene is selected the
   * Player still renders each clip's own logo via the engine's per-clip
   * pipeline, but the in-canvas drag handle hides — there is no "global"
   * thing left to grab in that case.
   */
  const selectedSceneWatermark = useMemo<WatermarkConfig | undefined>(() => {
    if (!watermark.enabled) return undefined;
    if (!selectedScene) return undefined;
    const eff = resolveSceneWatermark(watermark, selectedScene.inputs.extras);
    return {
      enabled: true,
      position: eff.position,
      opacity: eff.opacity,
      size: eff.size,
      paddingPx: eff.paddingPx,
      offsetX: eff.offsetX,
      offsetY: eff.offsetY,
      xPct: eff.xPct,
      yPct: eff.yPct,
      showOnAllScenes: false,
    };
  }, [watermark, selectedScene]);

  const addScene = (meta: SceneMeta): void => {
    const newId = newUid();
    const anyExplicit = scenes.some((s) => typeof s.fromSec === "number");
    const next: ReelScene = {
      uid: newId,
      sceneId: meta.id,
      durationSec: meta.defaultDurationSec,
      naturalDurationSec: meta.defaultDurationSec,
      inputs: { ...meta.defaultInputs },
      transition: { kind: "none", durationFrames: 0 },
    };
    if (anyExplicit) {
      const layoutNow = resolveClipLayout(scenes, FPS);
      const endSec = layoutNow.reduce(
        (m, c) => Math.max(m, (c.fromFrames + c.durFrames) / FPS),
        0,
      );
      next.fromSec = Math.round(endSec * 10) / 10;
      next.track = 0;
    }
    setScenes((prev) => [...prev, next]);
    setSelectedUid(newId);
  };

  const updateScene = (uid: string, patch: ReelScene): void => {
    setScenes((prev) => prev.map((s) => (s.uid === uid ? patch : s)));
  };

  /**
   * Fügt mehrere fertig vorbereitete Szenen NACH der aktuell ausgewählten
   * Szene ein (oder ans Ende der Timeline, wenn nichts ausgewählt ist).
   * Bei Free-Form-Reels (mit expliziten `fromSec`) werden die nachfolgenden
   * Clips um die Gesamt-Dauer der eingefügten Szenen verschoben, damit nichts
   * überlappt. Beim ersten neuen Clip wird `fromSec` auf das Ende des
   * Anker-Clips gesetzt.
   */
  const insertScenesAfterSelected = (incoming: ReelScene[]): void => {
    if (incoming.length === 0) return;
    setScenes((prev) => {
      const anchorIdx = selectedUid ? prev.findIndex((s) => s.uid === selectedUid) : -1;
      const insertAt = anchorIdx >= 0 ? anchorIdx + 1 : prev.length;
      const anyExplicit = prev.some((s) => typeof s.fromSec === "number");

      const totalIncomingSec = incoming.reduce((acc, s) => acc + s.durationSec, 0);

      let nextScenes = prev.slice();
      if (anyExplicit) {
        const layoutNow = resolveClipLayout(prev, FPS);
        const anchorLayout = anchorIdx >= 0 ? layoutNow[anchorIdx] : null;
        const insertFromSec =
          anchorLayout != null
            ? (anchorLayout.fromFrames + anchorLayout.durFrames) / FPS
            : layoutNow.reduce(
                (m, c) => Math.max(m, (c.fromFrames + c.durFrames) / FPS),
                0,
              );

        // Shift subsequent clips (those that start at/after the insert point)
        // by `totalIncomingSec` so we don't overlap.
        nextScenes = prev.map((s, i) => {
          if (i <= anchorIdx) return s;
          if (typeof s.fromSec !== "number") return s;
          if (s.fromSec < insertFromSec - 0.001) return s;
          return { ...s, fromSec: Math.round((s.fromSec + totalIncomingSec) * 10) / 10 };
        });

        let runningFromSec = insertFromSec;
        const stamped = incoming.map((s) => {
          const placed: ReelScene = {
            ...s,
            fromSec: Math.round(runningFromSec * 10) / 10,
            track: s.track ?? 0,
          };
          runningFromSec += s.durationSec;
          return placed;
        });
        return [
          ...nextScenes.slice(0, insertAt),
          ...stamped,
          ...nextScenes.slice(insertAt),
        ];
      }

      // Legacy sequential layout — just splice in.
      return [
        ...nextScenes.slice(0, insertAt),
        ...incoming,
        ...nextScenes.slice(insertAt),
      ];
    });
    setSelectedUid(incoming[incoming.length - 1]?.uid ?? null);
  };

  const removeScene = (uid: string): void => {
    setScenes((prev) => prev.filter((s) => s.uid !== uid));
    if (selectedUid === uid) setSelectedUid(null);
  };

  // ---------- Overlay (text layer) helpers ----------

  /** Currently-selected overlay (if `selectedUid` matches one). */
  const selectedOverlay = useMemo(
    () => overlays.find((o) => o.uid === selectedUid) ?? null,
    [overlays, selectedUid],
  );

  /** True when `selectedUid` references an overlay (vs a scene). */
  const isOverlaySelected = !!selectedOverlay;

  /** Currently-selected SFX event (if `selectedUid` matches one). */
  const selectedSfxEvent = useMemo(
    () => sfxEvents.find((s) => s.uid === selectedUid) ?? null,
    [sfxEvents, selectedUid],
  );
  const isSfxSelected = !!selectedSfxEvent;

  /**
   * Generic add: pushes ANY overlay onto the timeline at the playhead. The
   * caller can override `fromSec`/`xPct`/`yPct` via `initial` (e.g. when a
   * library item is dropped on a specific spot in the preview).
   *
   * This is the single entrypoint for "give me a new layer with these
   * properties" — addText / addShape / addBadge etc. all funnel through it.
   */
  const addOverlay = useCallback(
    <T extends OverlayLayer>(layer: T): string => {
      setOverlays((prev) => [...prev, layer]);
      setSelectedUid(layer.uid);
      return layer.uid;
    },
    [],
  );

  /** Returns common defaults derived from the current playhead. */
  const overlayBaseDefaults = useCallback(
    (): { uid: string; fromSec: number; durSec: number; track: number; xPct: number; yPct: number; scale: number; rotate: number; opacity: number } => ({
      uid: `o${Date.now()}_${++__uid}`,
      fromSec: Math.round((playheadFrame / FPS) * 10) / 10,
      durSec: 3,
      track: 10,
      xPct: 50,
      yPct: 50,
      scale: 1,
      rotate: 0,
      opacity: 1,
    }),
    [playheadFrame],
  );

  /** Add a new text overlay at the playhead with sensible defaults. */
  const addTextOverlay = useCallback(
    (initial?: Partial<TextOverlayLayer>): string => {
      const next: TextOverlayLayer = {
        ...overlayBaseDefaults(),
        kind: "text",
        text: "Dein Text",
        fontSize: 96,
        weight: 800,
        color: "primary",
        align: "center",
        shadow: true,
        ...initial,
      };
      return addOverlay(next);
    },
    [addOverlay, overlayBaseDefaults],
  );

  /**
   * Patch ANY overlay. The patch must be a subset of the matching kind's
   * fields; we merge it into the existing record and preserve the discriminator
   * so the cast back to the union stays sound.
   */
  const updateOverlay = useCallback(
    (uid: string, patch: Partial<OverlayLayer>): void => {
      setOverlays((prev) =>
        prev.map((o) => {
          if (o.uid !== uid) return o;
          // Preserve `kind` (discriminator) — patches should never change it.
          const merged = { ...o, ...patch, kind: o.kind } as OverlayLayer;
          return merged;
        }),
      );
    },
    [],
  );

  const removeOverlay = useCallback(
    (uid: string): void => {
      setOverlays((prev) => prev.filter((o) => o.uid !== uid));
      if (selectedUid === uid) setSelectedUid(null);
    },
    [selectedUid],
  );

  const duplicateOverlay = useCallback(
    (uid: string): void => {
      const src = overlays.find((o) => o.uid === uid);
      if (!src) return;
      const newId = `o${Date.now()}_${++__uid}`;
      const copy = {
        ...src,
        uid: newId,
        fromSec: Math.round((src.fromSec + Math.min(0.5, src.durSec / 4)) * 10) / 10,
      } as OverlayLayer;
      setOverlays((prev) => [...prev, copy]);
      setSelectedUid(newId);
    },
    [overlays],
  );

  /* ---------- SFX-Events: CRUD ---------- */

  const addSfxEvent = useCallback(
    (
      sound: SfxName,
      fromSec: number,
      durSec: number,
      custom?: { src: string; assetId?: string; label?: string },
    ): SfxEvent => {
      const newId = `x${Date.now()}_${++__uid}`;
      const ev: SfxEvent = {
        uid: newId,
        sound,
        fromSec: Math.max(0, fromSec),
        durSec: Math.max(0.1, durSec),
        volume: custom ? 0.85 : 0.6,
        ...(custom
          ? {
              src: custom.src,
              assetId: custom.assetId,
              label: custom.label,
              fadeInSec: 0.05,
              fadeOutSec: 0.1,
              trimStartSec: 0,
            }
          : {}),
      };
      setSfxEvents((prev) => [...prev, ev]);
      setSelectedUid(newId);
      return ev;
    },
    [],
  );

  /** Adds a custom-audio SFX event from the audio library. */
  const addCustomSfxEvent = useCallback(
    (asset: AudioAsset, fromSec: number): SfxEvent => {
      const dur = asset.durationSec > 0 ? asset.durationSec : 1.5;
      return addSfxEvent("click", fromSec, dur, {
        src: asset.url,
        assetId: asset.id,
        label: asset.displayName,
      });
    },
    [addSfxEvent],
  );

  /**
   * Auto-SFX: setzt das passende Sound-Rezept (siehe
   * `sceneSfxRecipes.ts`) auf eine einzelne Scene. Idempotent:
   * existieren bereits `sfxEvents` mit `sceneUid === uid`, werden sie
   * vorher entfernt (Toggle-Logik). Beim erneuten Klick bei sceneUid-
   * tagged Events wird also frisch nachjustiert (z. B. nach geänderter
   * Item-Anzahl in der Frage-Liste).
   */
  const applyAutoSfxForScene = useCallback(
    (sceneUid: string): void => {
      const sc = scenes.find((s) => s.uid === sceneUid);
      if (!sc) return;
      const clipLayout = layout.find((c) => c.scene.uid === sceneUid);
      const sceneFromSec = sc.fromSec ?? (clipLayout ? clipLayout.fromFrames / FPS : 0);
      const beats = buildSceneSfxRecipe({
        sceneId: sc.sceneId,
        inputs: sc.inputs ?? {},
        durationSec: sc.durationSec,
      });
      setSfxEvents((prev) => {
        const cleaned = prev.filter((e) => e.sceneUid !== sceneUid);
        const ts = Date.now().toString(36);
        const generated: SfxEvent[] = beats.map((b, i) => ({
          uid: `auto_${sceneUid}_${i}_${ts}`,
          fromSec: Math.max(0, sceneFromSec + b.atSec),
          durSec: Math.max(0.1, b.durSec ?? 0.6),
          sound: b.sound,
          volume: b.volume ?? 0.4,
          ...(b.label ? { label: b.label } : {}),
          sceneUid,
        }));
        return [...cleaned, ...generated];
      });
    },
    [scenes, layout],
  );

  /** Entfernt alle Auto-SFX-Beats, die zu dieser Scene gehören. */
  const clearAutoSfxForScene = useCallback((sceneUid: string): void => {
    setSfxEvents((prev) => prev.filter((e) => e.sceneUid !== sceneUid));
  }, []);

  /** Patch the music track from any source (panel inputs, timeline drags). */
  const patchMusic = useCallback((patch: Partial<MusicConfig>): void => {
    setMusic((prev) => {
      if (!prev) {
        // Lazy-init when the user starts dragging without ever opening the
        // panel — pick a sane default bed.
        return {
          bed: "ambient",
          volume: 0.18,
          enabled: true,
          fromSec: 0,
          loop: true,
          ...patch,
        } as MusicConfig;
      }
      return { ...prev, ...patch };
    });
  }, []);

  /**
   * Patch the voiceover track (Track-Level: volume/label/enabled/src/...).
   * Wenn der Patch Felder beinhaltet, die NICHT pro Segment existieren,
   * landen sie direkt auf der Config. Patches mit nur Legacy-Timeline-
   * Feldern (fromSec/durSec/trimStartSec/fadeInSec/fadeOutSec) werden
   * automatisch an das ERSTE Segment weitergereicht — so funktioniert das
   * VoiceoverPanel weiterhin auch ohne Segment-IDs zu kennen, und Timeline-
   * Drag-Handler können die Single-Segment-Spur normal manipulieren.
   */
  const patchVoiceover = useCallback((patch: Partial<VoiceoverConfig>): void => {
    setVoiceover((prev) => {
      if (!prev) return prev;
      const ensured = voEnsureSegments(prev);
      const segs = ensured.segments ?? [];
      const segLevelKeys = ["fromSec", "durSec", "trimStartSec", "fadeInSec", "fadeOutSec"] as const;
      const segPatch: Partial<Record<typeof segLevelKeys[number], number>> = {};
      const cfgPatch: Partial<VoiceoverConfig> = { ...patch };
      let hasSegPatch = false;
      for (const k of segLevelKeys) {
        if (k in patch) {
          const v = patch[k];
          if (typeof v === "number") {
            segPatch[k] = v;
            hasSegPatch = true;
          }
          delete cfgPatch[k];
        }
      }
      let next: VoiceoverConfig = { ...ensured, ...cfgPatch };
      if (hasSegPatch && segs[0]) {
        next = voPatchSegment(next, segs[0].id, segPatch);
      }
      return next;
    });
  }, []);

  /** Patcht GENAU ein Voiceover-Segment per id (vom Timeline-Bar genutzt). */
  const patchVoiceoverSegment = useCallback(
    (id: string, patch: Partial<{ fromSec: number; durSec: number; trimStartSec: number; fadeInSec: number; fadeOutSec: number }>): void => {
      setVoiceover((prev) => (prev ? voPatchSegment(voEnsureSegments(prev), id, patch) : prev));
    },
    [],
  );

  /** Splittet das aktuell den Playhead enthaltende VO-Segment in zwei
   *  Stücke (Blade-Tool). Wenn der Playhead nicht innerhalb eines
   *  Segments steht, passiert nichts. */
  const splitVoiceoverAtPlayhead = useCallback((): void => {
    setVoiceover((prev) => {
      if (!prev) return prev;
      const playSec = playheadFrame / FPS;
      const result = voSplitAtPlayhead(voEnsureSegments(prev), playSec);
      if (result.newSegmentId) {
        setSelectedUid(`vo:${result.newSegmentId}`);
      }
      return result.config;
    });
  }, [playheadFrame]);

  /** Entfernt ein Voiceover-Segment per ID. Wenn das letzte Segment
   *  entfernt wird, wird die ganze Voiceover-Spur gelöscht — sauberer
   *  als eine "leere" Voiceover-Config zurückzulassen. */
  const removeVoiceoverSegment = useCallback((id: string): void => {
    setVoiceover((prev) => {
      if (!prev) return prev;
      const next = voRemoveSegment(voEnsureSegments(prev), id);
      if (!next.segments || next.segments.length === 0) return undefined;
      return next;
    });
    if (selectedUid === `vo:${id}`) setSelectedUid(null);
  }, [selectedUid]);

  /** Kümmert sich nach Abschluss einer Mikrofon-Aufnahme um Decode +
   *  WAV-Encoding + Upload + Platzierung als VO-Segment. Ruft
   *  `setVoiceover` nur EINMAL ganz am Ende auf, damit während der
   *  Aufnahme kein Player-Re-render stattfindet (vermeidet Stottern
   *  beim Lip-Sync). */
  const handleRecordingFinished = useCallback(async (res: StopResult): Promise<void> => {
    try {
      // 1) Decode webm/opus → AudioBuffer (im Worker-thread des Browsers)
      const audioBuf = await decodeBlob(res.rawBlob);
      // Echtdauer aus dem Buffer ist genauer als unsere performance.now()-Messung
      const realDurSec = audioBuf.duration;

      // 2) WAV-Blob bauen (verlustfrei, Engine + Producer mögen WAV nativ)
      const wavBlob = encodeWav(audioBuf);
      // Aussagekräftiger Filename — Datum + Reel-Position helfen beim
      // späteren Wiederfinden in der Audio-Library.
      const ts = new Date();
      const stamp = `${ts.getFullYear()}${String(ts.getMonth() + 1).padStart(2, "0")}${String(ts.getDate()).padStart(2, "0")}_${String(ts.getHours()).padStart(2, "0")}${String(ts.getMinutes()).padStart(2, "0")}${String(ts.getSeconds()).padStart(2, "0")}`;
      const fileName = `voiceover_${stamp}.wav`;
      const file = new File([wavBlob], fileName, { type: "audio/wav" });

      // 3) Upload in die Audio-Library als "voiceover"-Asset.
      const upload = await api.audioUpload([file], "voiceover");
      const asset = upload.uploaded[0];
      if (!asset) throw new Error("Upload lieferte kein Asset zurück.");

      // Audio-Library kennt die Dauer nach dem reinen Upload nicht — wir
      // patchen sie nachträglich (fire-and-forget, damit der UI-Flow nicht
      // hängt), sodass das Asset später im AudioLibrary-Picker korrekt
      // mit Sekunden angezeigt wird.
      void api.audioPatch(asset.id, { durationSec: realDurSec }).catch(() => undefined);

      // 4) Als neues VO-Segment platzieren — exakt am Player-Zeitpunkt
      //    von Recording-Start (lip-sync-perfekt).
      setVoiceover((prev) => {
        const newSeg = {
          id: voNewSegmentId(),
          fromSec: Math.max(0, res.playerStartSec),
          durSec: realDurSec,
          trimStartSec: 0,
          fadeInSec: 0,
          fadeOutSec: 0,
          src: asset.url,
          assetId: asset.id,
        };
        if (!prev) {
          // Frische VO-Spur anlegen — Track-Level src zeigt auf das gleiche
          // Asset, damit Legacy-Reader korrekt funktionieren.
          return {
            src: asset.url,
            assetId: asset.id,
            label: asset.displayName ?? "Voiceover",
            volume: 1,
            enabled: true,
            segments: [newSeg],
          };
        }
        const ensured = voEnsureSegments(prev);
        return {
          ...ensured,
          enabled: true,
          segments: [...(ensured.segments ?? []), newSeg],
        };
      });
      setSidebarTab("voiceover");
    } catch (e) {
      // Eigenes Error-Bookkeeping reicht nicht — wir poppen es im Status auf,
      // damit der User nicht im Dunkeln bleibt.
      const msg = e instanceof Error ? e.message : String(e);
      setErr(`Mic-Aufnahme konnte nicht gespeichert werden: ${msg}`);
    }
  }, []);

  // ---- ElevenLabs-Vertonung: Übernahme generierter Assets ins Reel ----

  /** Platziert ein (TTS-/Voice-Changer-)Asset als VO-Segment an `fromSec`. */
  const placeVoiceoverAsset = useCallback(
    (asset: AudioAsset, fromSec: number, durSec: number): void => {
      setVoiceover((prev) => {
        const newSeg = {
          id: voNewSegmentId(),
          fromSec: Math.max(0, fromSec),
          durSec: Math.max(0.2, durSec),
          trimStartSec: 0,
          fadeInSec: 0,
          fadeOutSec: 0,
          src: asset.url,
          assetId: asset.id,
        };
        if (!prev) {
          return {
            src: asset.url,
            assetId: asset.id,
            label: asset.displayName ?? "Voiceover",
            volume: 1,
            enabled: true,
            segments: [newSeg],
          };
        }
        const ensured = voEnsureSegments(prev);
        return { ...ensured, enabled: true, segments: [...(ensured.segments ?? []), newSeg] };
      });
      setSidebarTab("voiceover");
    },
    [],
  );

  /** Setzt ein generiertes Musikstück als Music-Spur. */
  const setMusicFromAsset = useCallback((asset: AudioAsset): void => {
    setMusic((prev) => ({
      bed: undefined,
      src: asset.url,
      assetId: asset.id,
      label: asset.displayName,
      // Wie der Custom-Upload-Pfad im MusicPanel: 0.6 als Default, damit die
      // erzeugte Musik hörbar ist (nicht der leise 0.18-Bed-Default).
      volume: prev?.volume ?? 0.6,
      enabled: true,
      fromSec: prev?.fromSec ?? 0,
      durSec: asset.durationSec > 0 ? asset.durationSec : undefined,
      trimStartSec: 0,
      fadeInSec: prev?.fadeInSec ?? 0.4,
      fadeOutSec: prev?.fadeOutSec ?? 0.8,
      loop: prev?.loop ?? false,
    }));
    setSidebarTab("music");
  }, []);

  /** Ersetzt die VO-Spur durch eine komplette Auto-Vertonung (Segment/Szene). */
  const applyAutoScore = useCallback(
    (
      segments: {
        sceneUid: string;
        fromSec: number;
        durSec: number;
        src: string;
        assetId: string;
        label: string;
      }[],
    ): void => {
      if (!segments.length) return;
      const segs = segments.map((s) => ({
        id: voNewSegmentId(),
        fromSec: Math.max(0, s.fromSec),
        durSec: Math.max(0.2, s.durSec),
        trimStartSec: 0,
        fadeInSec: 0,
        fadeOutSec: 0,
        src: s.src,
        assetId: s.assetId,
      }));
      setVoiceover({
        src: segments[0]!.src,
        assetId: segments[0]!.assetId,
        label: "Auto-Vertonung",
        volume: 1,
        enabled: true,
        segments: segs,
      });
      setSidebarTab("voiceover");
    },
    [],
  );

  /** Spielt die Vorschau ohne Re-mount ab (genau das, was der TransportBar-
   *  Play-Button macht). Wird vom MicRecorder genutzt, um beim Recording
   *  automatisch die Reel-Vorschau mitlaufen zu lassen. */
  const playPreviewSilent = useCallback((): void => {
    if (!player) return;
    try { player.play(); } catch { /* ignore */ }
  }, [player]);
  const pausePreviewSilent = useCallback((): void => {
    if (!player) return;
    try { player.pause(); } catch { /* ignore */ }
  }, [player]);

  /** Lädt die aktuelle (oder eine spezifische) VO-Audio-URL als WAV bzw.
   *  MP3 herunter — nutzt die existierende Datei wenn möglich, sonst
   *  decode + re-encode. */
  const handleExportVoiceover = useCallback(
    async (format: "wav" | "mp3", srcUrl?: string): Promise<void> => {
      try {
        const url = srcUrl ?? voiceover?.src;
        if (!url) {
          setErr("Keine Voiceover-Datei zum Export.");
          return;
        }
        const res = await fetch(url, { credentials: "include" });
        if (!res.ok) throw new Error(`Fetch fehlgeschlagen (${res.status})`);
        const blob = await res.blob();
        const audioBuf = await decodeBlob(blob);
        const ts = new Date();
        const stamp = `${ts.getFullYear()}${String(ts.getMonth() + 1).padStart(2, "0")}${String(ts.getDate()).padStart(2, "0")}_${String(ts.getHours()).padStart(2, "0")}${String(ts.getMinutes()).padStart(2, "0")}${String(ts.getSeconds()).padStart(2, "0")}`;
        if (format === "wav") {
          downloadBlob(encodeWav(audioBuf), `voiceover_${stamp}.wav`);
        } else {
          downloadBlob(encodeMp3(audioBuf, 192), `voiceover_${stamp}.mp3`);
        }
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        setErr(`Export fehlgeschlagen: ${msg}`);
      }
    },
    [voiceover?.src],
  );

  const updateSfxEvent = useCallback(
    (uid: string, patch: Partial<SfxEvent>): void => {
      setSfxEvents((prev) =>
        prev.map((s) => (s.uid === uid ? { ...s, ...patch, uid: s.uid } : s)),
      );
    },
    [],
  );

  const removeSfxEvent = useCallback(
    (uid: string): void => {
      setSfxEvents((prev) => prev.filter((s) => s.uid !== uid));
      if (selectedUid === uid) setSelectedUid(null);
    },
    [selectedUid],
  );

  const duplicateSfxEvent = useCallback(
    (uid: string): void => {
      const src = sfxEvents.find((s) => s.uid === uid);
      if (!src) return;
      const newId = `x${Date.now()}_${++__uid}`;
      const copy: SfxEvent = {
        ...src,
        uid: newId,
        fromSec: Math.round((src.fromSec + Math.min(0.4, src.durSec / 4)) * 10) / 10,
      };
      setSfxEvents((prev) => [...prev, copy]);
      setSelectedUid(newId);
    },
    [sfxEvents],
  );

  /** Szene-Vorlagen-SFX: einzelnen Beat stumm schalten (extras.embeddedSfxSuppress). */
  const suppressEmbeddedSfxBeat = useCallback((sceneUid: string, embedIndex: number): void => {
    setScenes((prev) =>
      prev.map((s) => {
        if (s.uid !== sceneUid) return s;
        const extras = { ...(s.inputs.extras ?? {}) };
        const idxSet = new Set(
          String(extras.embeddedSfxSuppress ?? "")
            .split(",")
            .map((x) => parseInt(x.trim(), 10))
            .filter((n) => !Number.isNaN(n)),
        );
        idxSet.add(embedIndex);
        extras.embeddedSfxSuppress = [...idxSet].sort((a, b) => a - b).join(",");
        return { ...s, inputs: { ...s.inputs, extras } };
      }),
    );
    setSelectedUid(null);
  }, []);

  /** Fallback-Chip ohne Rezept-Karte: extras.sfx = false für diese Szene. */
  const muteEmbeddedBundledSceneSfx = useCallback((sceneUid: string): void => {
    setScenes((prev) =>
      prev.map((s) => {
        if (s.uid !== sceneUid) return s;
        const extras = { ...(s.inputs.extras ?? {}), sfx: false };
        return { ...s, inputs: { ...s.inputs, extras } };
      }),
    );
    setSelectedUid(null);
  }, []);

  /** Replace the entire overlays array — used by the LayersPanel when the
   *  user re-stacks layers via drag-and-drop. The panel emits a fully
   *  re-tracked array so the renderer immediately picks up the new z-order. */
  const replaceOverlays = useCallback(
    (next: OverlayLayer[]): void => {
      setOverlays(next);
    },
    [],
  );

  /** Apply the same patch to many overlays at once. Used by the Layers
   *  panel's bulk hide / lock toggles. */
  const bulkPatchOverlays = useCallback(
    (uids: string[], patch: Partial<OverlayLayer>): void => {
      const set = new Set(uids);
      setOverlays((prev) =>
        prev.map((o) =>
          set.has(o.uid)
            ? ({ ...o, ...patch, kind: o.kind } as OverlayLayer)
            : o,
        ),
      );
    },
    [],
  );

  /** Remove many overlays at once. */
  const bulkRemoveOverlays = useCallback(
    (uids: string[]): void => {
      const set = new Set(uids);
      setOverlays((prev) => prev.filter((o) => !set.has(o.uid)));
      setSelectedUids((prev) => {
        const next = new Set(prev);
        for (const u of uids) next.delete(u);
        return next;
      });
      setSelectedUidRaw((prev) => (prev && set.has(prev) ? null : prev));
    },
    [],
  );

  /** Wrap a set of overlays into a new Photoshop-style group. The group
   *  inherits the earliest fromSec / latest endSec of its members so that
   *  toggling it on the timeline visually masks the right slot. */
  const groupOverlays = useCallback(
    (uids: string[]): string | null => {
      if (uids.length === 0) return null;
      const set = new Set(uids);
      const members = overlays.filter((o) => set.has(o.uid));
      if (members.length === 0) return null;
      const groupUid = `g${Date.now()}_${++__uid}`;
      const fromSec = Math.min(...members.map((m) => m.fromSec));
      const endSec = Math.max(...members.map((m) => m.fromSec + m.durSec));
      const groupLayer = {
        uid: groupUid,
        kind: "group" as const,
        fromSec,
        durSec: Math.max(0.2, endSec - fromSec),
        // High track so the group sits above its members in the panel order.
        track: Math.min(20, Math.max(...members.map((m) => m.track), 0) + 1),
        xPct: 50,
        yPct: 50,
        scale: 1,
        rotate: 0,
        opacity: 1,
        expanded: true,
        name: `Gruppe ${members.length}`,
      };
      setOverlays((prev) => [
        groupLayer,
        ...prev.map((o) => (set.has(o.uid) ? { ...o, parentUid: groupUid } : o)),
      ]);
      setSelectedUidRaw(groupUid);
      setSelectedUids(new Set([groupUid]));
      return groupUid;
    },
    [overlays],
  );

  /** Pop members out of their group (or remove a group entirely). When the
   *  uid is a group, every child loses its parentUid AND the group itself
   *  is removed from the timeline. */
  const ungroupOverlays = useCallback(
    (uids: string[]): void => {
      const set = new Set(uids);
      setOverlays((prev) => {
        const groupUidsBeingRemoved = new Set<string>();
        for (const o of prev) {
          if (o.kind === "group" && set.has(o.uid)) groupUidsBeingRemoved.add(o.uid);
        }
        return prev
          .map((o) => {
            // Children losing their (selected-or-removed-group) parent.
            if (
              o.parentUid &&
              (set.has(o.parentUid) ||
                groupUidsBeingRemoved.has(o.parentUid) ||
                set.has(o.uid))
            ) {
              const { parentUid: _drop, ...rest } = o;
              void _drop;
              return rest as OverlayLayer;
            }
            return o;
          })
          .filter((o) => !(o.kind === "group" && groupUidsBeingRemoved.has(o.uid)));
      });
    },
    [],
  );

  /** Duplicate many overlays in-place — each clone gets a new uid and is
   *  selected as the new primary set. */
  const bulkDuplicateOverlays = useCallback(
    (uids: string[]): void => {
      const set = new Set(uids);
      const newSelection: string[] = [];
      setOverlays((prev) => {
        const additions: OverlayLayer[] = [];
        for (const o of prev) {
          if (!set.has(o.uid)) continue;
          const newId = `o${Date.now()}_${++__uid}`;
          newSelection.push(newId);
          additions.push({
            ...o,
            uid: newId,
            fromSec:
              Math.round((o.fromSec + Math.min(0.5, o.durSec / 4)) * 10) / 10,
          } as OverlayLayer);
        }
        return [...prev, ...additions];
      });
      // Defer selection to after the state commit so the new uids exist.
      setTimeout(() => {
        setSelectedUids(new Set(newSelection));
        setSelectedUidRaw(newSelection[newSelection.length - 1] ?? null);
      }, 0);
    },
    [],
  );

  /**
   * Add a library element (already serialized as a partial overlay template)
   * at the playhead. This is the integration point for the Element Library +
   * drag&drop: the library passes a template missing `uid`/`fromSec` and the
   * Composer fills them in from the current playhead and an optional drop
   * position (xPct/yPct in composition space).
   */
  const addOverlayFromTemplate = useCallback(
    (template: Omit<OverlayLayer, "uid" | "fromSec"> & Partial<Pick<OverlayLayer, "fromSec">>): string => {
      const base = overlayBaseDefaults();
      const next = {
        ...template,
        uid: base.uid,
        fromSec: template.fromSec ?? base.fromSec,
      } as OverlayLayer;
      return addOverlay(next);
    },
    [addOverlay, overlayBaseDefaults],
  );

  /**
   * Patch the watermark overrides for a single scene. Stored under
   * `inputs.extras.wm*` (siehe `app/src/lib/sceneWatermark.ts`).
   * Setting a value to `undefined` deletes that key — the scene then
   * falls back to the global default for that field.
   *
   * Called from:
   *  - the Inspector's „🎯 Logo in dieser Scene" section (slider edits),
   *  - the PlayerEditOverlay's logo drag/resize (commits on pointerup).
   */
  const patchSceneWatermark = useCallback(
    (
      uid: string,
      patch: {
        wmPosition?: WatermarkPosition;
        wmOpacity?: number;
        wmSize?: number;
        wmPaddingPx?: number;
        wmOffsetX?: number;
        wmOffsetY?: number;
        wmXPct?: number;
        wmYPct?: number;
      },
    ): void => {
      setScenes((prev) =>
        prev.map((s) => {
          if (s.uid !== uid) return s;
          const extras: Record<string, string | number | boolean> = { ...(s.inputs.extras ?? {}) };
          for (const [k, v] of Object.entries(patch)) {
            if (v === undefined) delete extras[k];
            else extras[k] = v as string | number | boolean;
          }
          return { ...s, inputs: { ...s.inputs, extras } };
        }),
      );
    },
    [],
  );

  /** Patch the selected scene's positional extras (offsetX/Y, scale).
   *  Used by the in-image drag overlay (Scene-Box drag + Scale-Handle). */
  const patchSceneExtras = useCallback(
    (uid: string, patch: { offsetX?: number; offsetY?: number; scale?: number }): void => {
      setScenes((prev) =>
        prev.map((s) => {
          if (s.uid !== uid) return s;
          const extras: Record<string, string | number | boolean> = { ...(s.inputs.extras ?? {}) };
          if (patch.offsetX !== undefined) extras.offsetX = patch.offsetX;
          if (patch.offsetY !== undefined) extras.offsetY = patch.offsetY;
          if (patch.scale !== undefined) extras.scale = patch.scale;
          return { ...s, inputs: { ...s.inputs, extras } };
        }),
      );
    },
    [],
  );

  /** Toggle Photoshop-style hidden/locked + Audio-Mute flags for a scene (LayersPanel). */
  const patchSceneVisibility = useCallback(
    (
      uid: string,
      patch: { hidden?: boolean; locked?: boolean; muteSfx?: boolean; muteMusic?: boolean },
    ): void => {
      setScenes((prev) =>
        prev.map((s) => {
          if (s.uid !== uid) return s;
          const extras: Record<string, string | number | boolean> = { ...(s.inputs.extras ?? {}) };
          if (patch.hidden !== undefined) extras.hidden = patch.hidden;
          if (patch.locked !== undefined) extras.locked = patch.locked;
          // Audio-Mutes folgen der bestehenden Konvention:
          // - SFX-Mute: extras.sfx === false (existing flag — Inspector + embedded SFX teilen sich das)
          // - Music-Mute: extras.muteMusic === true (neu)
          if (patch.muteSfx !== undefined) extras.sfx = !patch.muteSfx;
          if (patch.muteMusic !== undefined) extras.muteMusic = patch.muteMusic;
          return { ...s, inputs: { ...s.inputs, extras } };
        }),
      );
    },
    [],
  );

  /** Replace the entire scenes array (drag-reorder in LayersPanel). */
  const replaceScenes = useCallback((next: ReelScene[]): void => {
    setScenes(next);
  }, []);

  /**
   * Auto-pick the topmost clip on the FIRST hydration only (so the inspector
   * isn't blank when you first open a saved reel). After that we never
   * second-guess the user's selection — explicit deselect (ESC / bg click)
   * stays empty, and `addScene` / `duplicateScene` set selection themselves.
   */
  const didInitialPickRef = useRef(false);
  useEffect(() => {
    if (didInitialPickRef.current) return;
    if (layout.length === 0) return;
    didInitialPickRef.current = true;
    if (selectedUid) return;
    setSelectedUid(layout[0].scene.uid);
  }, [layout, selectedUid]);

  /**
   * Split a clip at the current playhead. The original keeps the head, a new
   * clip with the same content takes the tail. Both end up on the same track
   * in free-form mode (we migrate if needed). No-op if the playhead isn't
   * strictly inside the clip's interior (with a small epsilon).
   */
  const splitSceneAtPlayhead = useCallback((uid: string): void => {
    const idx = scenes.findIndex((s) => s.uid === uid);
    if (idx < 0) return;

    const anyExplicit = scenes.some((s) => typeof s.fromSec === "number");
    const base = anyExplicit
      ? scenes
      : (() => {
          const layoutNow = resolveClipLayout(scenes, FPS);
          return scenes.map((s, i) => ({
            ...s,
            fromSec: layoutNow[i].fromFrames / FPS,
            track: layoutNow[i].track,
          }));
        })();

    const src = base[idx];
    const fromSec = src.fromSec ?? 0;
    const playSec = playheadFrame / FPS;
    const localT = playSec - fromSec;
    const minPiece = 0.4;
    if (localT < minPiece || localT > src.durationSec - minPiece) return;

    const tailId = newUid();
    const head: ReelScene = { ...src, durationSec: Math.round(localT * 100) / 100 };
    const tail: ReelScene = {
      ...src,
      uid: tailId,
      inputs: { ...src.inputs, extras: { ...(src.inputs.extras ?? {}) } },
      fromSec: Math.round((fromSec + head.durationSec) * 100) / 100,
      durationSec: Math.round((src.durationSec - head.durationSec) * 100) / 100,
      track: src.track ?? 0,
    };
    const next = [...base];
    next.splice(idx, 1, head, tail);
    setScenes(next);
    setSelectedUid(tailId);
  }, [scenes, playheadFrame]);

  const duplicateScene = (uid: string): void => {
    const srcIdx = scenes.findIndex((s) => s.uid === uid);
    if (srcIdx < 0) return;
    const src = scenes[srcIdx];
    const newId = newUid();
    const copy: ReelScene = {
      ...src,
      uid: newId,
      inputs: { ...src.inputs, extras: { ...(src.inputs.extras ?? {}) } },
    };
    const anyExplicit = scenes.some((s) => typeof s.fromSec === "number");
    if (anyExplicit) {
      copy.fromSec = Math.round(((src.fromSec ?? 0) + src.durationSec) * 10) / 10;
      copy.track = src.track ?? 0;
    }
    setScenes((prev) => {
      const i = prev.findIndex((s) => s.uid === uid);
      if (i < 0) return prev;
      const next = [...prev];
      next.splice(i + 1, 0, copy);
      return next;
    });
    setSelectedUid(newId);
  };

  /**
   * Patch a clip's timeline position. When the user first drags a clip we
   * MIGRATE the entire reel from sequential to free-form layout so dragging
   * one clip doesn't accidentally re-flow the others. Snapping (when the
   * global snap toggle is on) clamps to 0.1s boundaries.
   */
  const patchSceneTime = useCallback(
    (
      uid: string,
      patch: { fromSec?: number; durationSec?: number; naturalDurationSec?: number; track?: number },
    ): void => {
      setScenes((prev) => {
        const anyExplicit = prev.some((s) => typeof s.fromSec === "number");
        // Migrate to free-form on first edit so previously-sequential clips
        // keep their visual positions.
        const base = anyExplicit
          ? prev
          : (() => {
              const layoutNow = resolveClipLayout(prev, FPS);
              return prev.map((s, i) => ({
                ...s,
                fromSec: layoutNow[i].fromFrames / FPS,
                track: layoutNow[i].track,
              }));
            })();
        const snapped = (n: number): number =>
          snap ? Math.round(n * 10) / 10 : Math.round(n * 100) / 100;
        return base.map((s) => {
          if (s.uid !== uid) return s;
          const next: ReelScene = { ...s };
          if (patch.fromSec !== undefined) next.fromSec = Math.max(0, snapped(patch.fromSec));
          if (patch.durationSec !== undefined)
            next.durationSec = Math.max(0.4, snapped(patch.durationSec));
          if (patch.naturalDurationSec !== undefined)
            next.naturalDurationSec = Math.max(0.4, snapped(patch.naturalDurationSec));
          if (patch.track !== undefined) next.track = Math.max(0, Math.min(8, Math.round(patch.track)));
          return next;
        });
      });
    },
    [snap],
  );

  /**
   * Move a scene one slot earlier ("up") or later ("down") in the timeline
   * sequence. Operates by sorting scenes by current `fromSec` (sequential
   * scenes are migrated to explicit fromSec first), swapping adjacent
   * neighbours, then re-laying out so the timeline stays gapless.
   *
   * The visual order of the result is exactly:
   *   sceneA → sceneB    becomes    sceneB → sceneA
   * with each clip re-anchored at the cumulative duration of its predecessors.
   */
  const moveSceneSequence = useCallback(
    (uid: string, direction: "up" | "down"): void => {
      setScenes((prev) => {
        if (prev.length < 2) return prev;
        // Migrate to explicit fromSec the same way patchSceneTime does, so
        // the sort below is well-defined for previously-sequential reels.
        const anyExplicit = prev.some((s) => typeof s.fromSec === "number");
        const explicit: ReelScene[] = anyExplicit
          ? prev
          : (() => {
              const layoutNow = resolveClipLayout(prev, FPS);
              return prev.map((s, i) => ({
                ...s,
                fromSec: layoutNow[i].fromFrames / FPS,
                track: layoutNow[i].track,
              }));
            })();

        // Stable sort by fromSec, then by original index so ties are broken
        // deterministically (matters when multiple clips share a track / time).
        const indexed = explicit.map((s, i) => ({ s, i }));
        indexed.sort((a, b) => {
          const fa = a.s.fromSec ?? 0;
          const fb = b.s.fromSec ?? 0;
          if (fa !== fb) return fa - fb;
          return a.i - b.i;
        });
        const order = indexed.map((x) => x.s);
        const idx = order.findIndex((s) => s.uid === uid);
        if (idx === -1) return prev;
        const swapWith = direction === "up" ? idx - 1 : idx + 1;
        if (swapWith < 0 || swapWith >= order.length) return prev;

        const next = order.slice();
        [next[idx], next[swapWith]] = [next[swapWith], next[idx]];

        // Re-anchor: lay everything out gaplessly along the same track each
        // clip already occupied — this gives the user a clean swap without
        // accidentally stomping carefully-placed offsets on other tracks.
        // We only re-anchor the two swapped clips; everything else stays put
        // so unrelated lanes / overlays don't shift.
        const a = next[swapWith]; // formerly later, now earlier
        const b = next[idx]; // formerly earlier, now later
        const earlierStart = Math.min(
          order[idx].fromSec ?? 0,
          order[swapWith].fromSec ?? 0,
        );
        const aDur = a.durationSec ?? 0;
        const newAFrom = earlierStart;
        const newBFrom = earlierStart + aDur;

        return prev.map((s) => {
          if (s.uid === a.uid) return { ...s, fromSec: Math.round(newAFrom * 100) / 100 };
          if (s.uid === b.uid) return { ...s, fromSec: Math.round(newBFrom * 100) / 100 };
          return s;
        });
      });
    },
    [],
  );

  // ---------- Clip clipboard (Cmd+C / Cmd+V) ----------
  const clipboardRef = useRef<ReelScene | null>(null);

  const copySelected = useCallback((): void => {
    const s = scenes.find((x) => x.uid === selectedUid);
    if (!s) return;
    clipboardRef.current = {
      ...s,
      inputs: { ...s.inputs, extras: { ...(s.inputs.extras ?? {}) } },
    };
  }, [scenes, selectedUid]);

  const pasteFromClipboard = useCallback((): void => {
    const src = clipboardRef.current;
    if (!src) return;
    setScenes((prev) => {
      const playSec = playheadFrame / FPS;
      const anyExplicit =
        prev.some((s) => typeof s.fromSec === "number") || typeof src.fromSec === "number";
      const copy: ReelScene = {
        ...src,
        uid: newUid(),
        inputs: { ...src.inputs, extras: { ...(src.inputs.extras ?? {}) } },
      };
      if (anyExplicit) {
        copy.fromSec = Math.round(playSec * 10) / 10;
        copy.track = src.track ?? 0;
      }
      setSelectedUid(copy.uid);
      return [...prev, copy];
    });
  }, [playheadFrame]);

  /** Nudge selected clip by `deltaSec` along the timeline. Auto-migrates to
   *  free-form on first use so existing sequential reels stay intact. */
  const nudgeSelected = useCallback(
    (deltaSec: number): void => {
      if (!selectedUid) return;
      const s = scenes.find((x) => x.uid === selectedUid);
      if (!s) return;
      const cur = layout.find((c) => c.scene.uid === selectedUid);
      const baseFrom = s.fromSec ?? (cur ? cur.fromFrames / FPS : 0);
      patchSceneTime(selectedUid, { fromSec: Math.max(0, baseFrom + deltaSec) });
    },
    [selectedUid, scenes, layout, patchSceneTime],
  );

  /** Global keyboard shortcuts — only active when no input/textarea is focused. */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (locked) return;
      const t = e.target as HTMLElement | null;
      const tag = t?.tagName ?? "";
      const isTyping =
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        tag === "SELECT" ||
        (t && (t.isContentEditable || t.closest("[contenteditable='true']"))) ||
        false;
      if (isTyping) return;

      const meta = e.metaKey || e.ctrlKey;

      // Deselect
      if (e.key === "Escape") {
        setSelectedUid(null);
        return;
      }
      // Delete selected (works for both scenes and overlays)
      if ((e.key === "Backspace" || e.key === "Delete") && selectedUid) {
        e.preventDefault();
        if (selectedUid.startsWith("vo:")) {
          removeVoiceoverSegment(selectedUid.slice(3));
          return;
        }
        if (selectedUid.startsWith("emb:")) {
          const rest = selectedUid.slice(4);
          const li = rest.lastIndexOf(":");
          if (li > 0) {
            const sceneUid = rest.slice(0, li);
            const tail = rest.slice(li + 1);
            if (tail === "fallback") muteEmbeddedBundledSceneSfx(sceneUid);
            else {
              const idx = parseInt(tail, 10);
              if (!Number.isNaN(idx)) suppressEmbeddedSfxBeat(sceneUid, idx);
            }
          }
          setSelectedUid(null);
          return;
        }
        if (sfxEvents.some((s) => s.uid === selectedUid)) {
          removeSfxEvent(selectedUid);
          return;
        }
        if (overlays.some((o) => o.uid === selectedUid)) removeOverlay(selectedUid);
        else removeScene(selectedUid);
        return;
      }
      // Duplicate selected (Cmd/Ctrl+D)
      if (meta && (e.key === "d" || e.key === "D") && selectedUid) {
        e.preventDefault();
        if (selectedUid.startsWith("emb:")) return;
        if (overlays.some((o) => o.uid === selectedUid)) duplicateOverlay(selectedUid);
        else if (sfxEvents.some((s) => s.uid === selectedUid)) duplicateSfxEvent(selectedUid);
        else duplicateScene(selectedUid);
        return;
      }
      // Quick-add text overlay (T) — only when nothing is being typed
      if ((e.key === "t" || e.key === "T") && !meta) {
        e.preventDefault();
        addTextOverlay();
        return;
      }
      // Copy / paste
      if (meta && (e.key === "c" || e.key === "C") && selectedUid) {
        e.preventDefault();
        if (selectedUid.startsWith("emb:")) return;
        copySelected();
        return;
      }
      if (meta && (e.key === "v" || e.key === "V")) {
        e.preventDefault();
        pasteFromClipboard();
        return;
      }
      // Split at playhead (S) — Blade-Tool. Wenn ein VO-Segment selektiert
      // ist, splitten wir die Voiceover-Spur am Playhead. Bei Scene-Selektion
      // splittet Scene-Splitter. Voiceover-Split funktioniert auch ohne
      // explizite Selektion: einfach Playhead positionieren + S drücken.
      if ((e.key === "s" || e.key === "S") && !meta) {
        if (selectedUid && !selectedUid.startsWith("vo:") && !selectedUid.startsWith("emb:")) {
          if (sfxEvents.some((s) => s.uid === selectedUid)) return;
          if (overlays.some((o) => o.uid === selectedUid)) return;
          e.preventDefault();
          splitSceneAtPlayhead(selectedUid);
          return;
        }
        if (voiceover && voiceover.enabled && voiceover.src) {
          e.preventDefault();
          splitVoiceoverAtPlayhead();
          return;
        }
      }
      // Nudge selected clip
      if (e.key === "ArrowLeft" && !meta && selectedUid) {
        e.preventDefault();
        if (selectedUid.startsWith("emb:")) return;
        nudgeSelected(e.shiftKey ? -1 : -0.1);
        return;
      }
      if (e.key === "ArrowRight" && !meta && selectedUid) {
        e.preventDefault();
        if (selectedUid.startsWith("emb:")) return;
        nudgeSelected(e.shiftKey ? 1 : 0.1);
        return;
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [
    locked,
    selectedUid,
    overlays,
    sfxEvents,
    voiceover,
    removeScene,
    duplicateScene,
    splitSceneAtPlayhead,
    splitVoiceoverAtPlayhead,
    removeVoiceoverSegment,
    copySelected,
    pasteFromClipboard,
    nudgeSelected,
    removeOverlay,
    duplicateOverlay,
    removeSfxEvent,
    duplicateSfxEvent,
    suppressEmbeddedSfxBeat,
    muteEmbeddedBundledSceneSfx,
    addTextOverlay,
  ]);

  const toggleFormat = (f: string): void => {
    setChosenFormats((cur) => (cur.includes(f) ? cur.filter((x) => x !== f) : [...cur, f]));
  };
  const toggleLocale = (l: string): void => {
    setChosenLocales((cur) => (cur.includes(l) ? cur.filter((x) => x !== l) : [...cur, l]));
  };

  const fitTo15 = (): void => {
    if (scenes.length === 0) return;
    const factor = DEFAULT_TARGET_SEC / Math.max(0.5, totalSec);
    setScenes((prev) =>
      prev.map((s) => ({ ...s, durationSec: Math.round(s.durationSec * factor * 10) / 10 })),
    );
  };

  // ---------- Workspace ops ----------
  /** Core save — used by both manual save button and auto-save. */
  const saveToWorkspace = useCallback(
    async (opts: { silent?: boolean } = {}): Promise<WorkspaceReel | null> => {
      if (scenes.length === 0) {
        if (!opts.silent) setErr("Speichern: mindestens eine Scene benötigt.");
        return null;
      }
      if (!opts.silent) setBusy(true);
      setAutoSaveStatus("saving");
      try {
        const saved = await api.workspaceSave({
          id: workspaceId ?? undefined,
          title,
          status,
          ideaId: initialIdea?.id,
          format: previewFormat as "9:16" | "1:1" | "16:9",
          theme,
          scenes,
          overlays,
          sfxEvents,
          sfxConfig,
          watermark,
          music,
          voiceover,
          notes,
          changeRequests,
        });
        hydrating.current = true;
        setWorkspaceId(saved.id);
        setSavedAt(saved.updatedAt);
        setNotes(saved.notes);
        setChangeRequests(saved.changeRequests);
        setDirty(false);
        setAutoSaveStatus("saved");
        if (!opts.silent) onSaved?.(saved);
        return saved;
      } catch (e) {
        setAutoSaveStatus("error");
        if (!opts.silent) setErr(`Speichern: ${(e as Error).message}`);
        return null;
      } finally {
        if (!opts.silent) setBusy(false);
      }
    },
    [
      scenes,
      overlays,
      sfxEvents,
      title,
      status,
      initialIdea,
      previewFormat,
      watermark,
      music,
      voiceover,
      notes,
      changeRequests,
      workspaceId,
      onSaved,
    ],
  );

  // ---------- DIRTY-INDIKATOR ----------
  // Auto-Save ist bewusst deaktiviert: Änderungen landen erst per
  // explizitem "Jetzt speichern" auf der Disk. Sobald `dirty` true ist,
  // setzen wir den Save-Chip auf "pending" und warnen beim Tab-Schließen.
  useEffect(() => {
    setAutoSaveStatus(dirty ? "pending" : autoSaveStatus === "saved" ? "saved" : "idle");
    // We deliberately omit autoSaveStatus from deps — this effect just
    // mirrors `dirty` and shouldn't loop on its own status writes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirty]);

  // After "saved" pulse, fade back to idle on next idle moment.
  useEffect(() => {
    if (autoSaveStatus !== "saved") return;
    const t = window.setTimeout(() => setAutoSaveStatus("idle"), 2200);
    return () => window.clearTimeout(t);
  }, [autoSaveStatus]);

  // Warn on tab/window close when there are unsaved changes.
  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent): void => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);

  const onChangeStatus = async (next: WorkspaceStatus): Promise<void> => {
    setStatus(next);
    if (workspaceId) {
      try {
        await api.workspaceSetStatus(workspaceId, next);
      } catch (e) {
        setErr(`Status: ${(e as Error).message}`);
      }
    }
  };

  const ensureSavedFor = async (label: string): Promise<string | null> => {
    if (workspaceId) return workspaceId;
    setErr(`${label}: bitte zuerst speichern.`);
    return null;
  };

  const addNote = async (text: string): Promise<void> => {
    const id = await ensureSavedFor("Notiz");
    if (!id) return;
    const r = await api.workspaceAddNote(id, text);
    setNotes(r.notes);
    setSavedAt(r.updatedAt);
  };
  const deleteNote = async (noteId: string): Promise<void> => {
    if (!workspaceId) return;
    const r = await api.workspaceDeleteNote(workspaceId, noteId);
    setNotes(r.notes);
    setSavedAt(r.updatedAt);
  };
  const addCR = async (text: string): Promise<void> => {
    const id = await ensureSavedFor("Change Request");
    if (!id) return;
    const r = await api.workspaceAddCR(id, text);
    setChangeRequests(r.changeRequests);
    setSavedAt(r.updatedAt);
  };
  const toggleCR = async (crId: string, done: boolean): Promise<void> => {
    if (!workspaceId) return;
    const r = await api.workspaceToggleCR(workspaceId, crId, done);
    setChangeRequests(r.changeRequests);
    setSavedAt(r.updatedAt);
  };
  const deleteCR = async (crId: string): Promise<void> => {
    if (!workspaceId) return;
    const r = await api.workspaceDeleteCR(workspaceId, crId);
    setChangeRequests(r.changeRequests);
    setSavedAt(r.updatedAt);
  };

  const submit = async (renderNow: boolean): Promise<void> => {
    setErr(null);
    if (scenes.length === 0) {
      setErr("Mindestens eine Scene auf der Timeline benötigt.");
      return;
    }
    if (chosenFormats.length === 0 || chosenLocales.length === 0) {
      setErr("Mindestens 1 Format und 1 Sprache wählen.");
      return;
    }
    setBusy(true);
    try {
      await api.addReelToQueue({
        title,
        ideaId: initialIdea?.id,
        category: initialIdea?.category ?? "reel",
        scenes,
        watermark,
        music,
        voiceover,
        overlays: overlays.length ? overlays : undefined,
        sfxEvents: sfxEvents.length ? sfxEvents : undefined,
        sfxConfig:
          sfxConfig.muted || Math.abs(sfxConfig.volume - 1) > 1e-3 ? sfxConfig : undefined,
        safeZone: safeZoneOn ? { enabled: true, platform: safeZonePlatform } : undefined,
        theme,
        languages: chosenLocales,
        formats: chosenFormats,
        renderNow,
      });
      const transportHint =
        Math.abs(speed - 1) > 0.001
          ? ` Hinweis: Vorschau lief mit ${speed}× — das MP4 entspricht Timeline-Geschwindigkeit 1× (wie Vorschau bei 1×).`
          : "";
      onQueued(
        renderNow
          ? `Reel "${title}" rendert im Hintergrund (${chosenLocales.length} × ${chosenFormats.length}) — du kannst weiterarbeiten.${transportHint}`
          : `Reel "${title}" in Queue (${chosenLocales.length} × ${chosenFormats.length}).${transportHint}`,
        renderNow,
      );
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const targetClass = totalSec > DEFAULT_TARGET_SEC + 0.5
    ? "warn"
    : totalSec < DEFAULT_TARGET_SEC - 0.5
      ? "muted"
      : "good";

  const statusMeta = STATUS_META[status];
  const openCRs = changeRequests.filter((c) => !c.done).length;

  // CSS variables drive the responsive grid (column widths shrink on
  // collapsed/floating panels, full breakpoint stacking happens via media).
  const dockGridStyle: React.CSSProperties = {
    ["--col-left" as string]:
      libMode === "docked" ? `${dock.state.colLeft}px`
      : libMode === "collapsed" ? "44px"
      : "0px",
    ["--col-right" as string]:
      insMode === "docked" ? `${dock.state.colRight}px`
      : insMode === "collapsed" ? "44px"
      : "0px",
  };

  return (
    <div
      className={`composer dock-grid v-${dock.viewport} lib-${libMode} ins-${insMode} tl-${tlMode}`}
      style={dockGridStyle}
    >
      <DockablePanel
        id="library"
        title="Library"
        icon="📚"
        mode={libMode}
        float={dock.state.floats.library}
        onToggleCollapse={() => dock.cycleCollapse("library")}
        onTogglePopup={() => dock.togglePopup("library")}
        onFloatChange={(r) => dock.setFloat("library", r)}
        popupTitle="Library als Popup öffnen"
        collapseTitle="Library minimieren"
        className="composer-library"
      >
        <div className="composer-library-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={libraryTab === "scenes"}
            className={`composer-library-tab ${libraryTab === "scenes" ? "active" : ""}`}
            onClick={() => setLibraryTab("scenes")}
            title="Scene-Bibliothek"
          >
            🎬 Scenes
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={libraryTab === "elements"}
            className={`composer-library-tab ${libraryTab === "elements" ? "active" : ""}`}
            onClick={() => setLibraryTab("elements")}
            title="Text-Presets, Shapes, Icons & Badges — ziehbar in den Preview"
          >
            ✦ Elements
          </button>
        </div>
        {libraryTab === "scenes" ? (
          <SceneLibrary scenes={scenesCatalog} onAdd={addScene} />
        ) : (
          <ElementLibrary
            onAdd={(template) => addOverlayFromTemplate(template)}
          />
        )}
      </DockablePanel>

      {!isMobile && libMode === "docked" && (
        <ColumnResizer side="left" current={dock.state.colLeft} onChange={(px) => dock.setColumn("left", px)} />
      )}

      <section className="composer-stage">
        <Toolbar className="stage-head" ariaLabel="Reel-Kopfzeile">
          <input
            className="title-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Titel des Reels …"
          />

          <ToolbarSpacer />

          <IconButton
            icon="↶"
            ariaLabel="Rückgängig"
            disabled={!canUndo}
            onClick={undo}
            title="Rückgängig (⌘Z / Ctrl+Z)"
            variant="ghost"
            size="sm"
          />
          <IconButton
            icon="↷"
            ariaLabel="Wiederherstellen"
            disabled={!canRedo}
            onClick={redo}
            title="Wiederherstellen (⇧⌘Z / Ctrl+Y)"
            variant="ghost"
            size="sm"
          />

          <ToolbarSep />

          <IconButton
            icon={busy ? "⏳" : dirty ? "●" : "✓"}
            label={dirty ? "Speichern" : "Gespeichert"}
            showLabel
            onClick={() => { void saveToWorkspace(); }}
            disabled={busy || (!dirty && autoSaveStatus !== "error")}
            title={
              dirty
                ? "Ungespeicherte Änderungen — jetzt persistieren (Auto-Save ist aus)"
                : autoSaveStatus === "error"
                ? "Letzter Save-Versuch fehlgeschlagen — erneut versuchen"
                : "Alles gespeichert"
            }
            variant={dirty ? "primary" : "ghost"}
          />

          {/* === Status-Chip (Workspace-Status + letzte Save-Info) === */}
          <Popover
            placement="bottom-end"
            width={260}
            trigger={({ open, toggle, ref }) => (
              <button
                ref={ref}
                type="button"
                className={`save-chip state-${autoSaveStatus} ${open ? "is-open" : ""} ${dirty ? "is-dirty" : ""}`}
                onClick={toggle}
                title={`Status: ${statusMeta.label}\n${autoSaveLabel(autoSaveStatus, workspaceId, savedAt)}`}
              >
                <span className="save-chip-dot" style={{ background: statusMeta.color }} />
                <span className="save-chip-label">{statusMeta.label}</span>
                <span className={`save-chip-sub state-${autoSaveStatus}`}>
                  {autoSaveStatus === "saving" ? "…" :
                   autoSaveStatus === "saved" ? "✓" :
                   autoSaveStatus === "error" ? "⚠" :
                   autoSaveStatus === "pending" ? "●" : "·"}
                </span>
                <span className="save-chip-caret" aria-hidden>▾</span>
              </button>
            )}
          >
            {({ close }) => (
              <>
                <MenuSection title="Status">
                  {ALL_STATUSES.map((s) => (
                    <MenuItem
                      key={s}
                      icon={<span className="status-dot" style={{ background: STATUS_META[s].color }} />}
                      label={STATUS_META[s].label}
                      active={status === s}
                      onClick={() => { void onChangeStatus(s); close(); }}
                    />
                  ))}
                </MenuSection>
                <MenuDivider />
                <MenuSection title="Workspace">
                  <div className="save-meta">
                    <div>{autoSaveLabel(autoSaveStatus, workspaceId, savedAt)}</div>
                    {workspaceId && <div className="save-meta-id">ID: {workspaceId}</div>}
                  </div>
                  <MenuItem
                    icon="💾"
                    label={dirty ? "Jetzt speichern" : "Erneut speichern"}
                    onClick={() => { void saveToWorkspace(); close(); }}
                  />
                </MenuSection>
              </>
            )}
          </Popover>

          {/* === Preview-Format als ein Dropdown === */}
          <Popover
            placement="bottom-end"
            width={170}
            trigger={({ open, toggle, ref }) => (
              <button
                ref={ref}
                type="button"
                className={`format-chip ${open ? "is-open" : ""}`}
                onClick={toggle}
                title="Vorschau-Format wechseln"
              >
                <span className="format-chip-icon" aria-hidden>{
                  previewFormat === "9:16" ? "📱" : previewFormat === "1:1" ? "⬛" : "🖥"
                }</span>
                <span>{previewFormat}</span>
                <span className="save-chip-caret" aria-hidden>▾</span>
              </button>
            )}
          >
            {({ close }) => (
              <MenuSection title="Vorschau-Format">
                {(["9:16", "1:1", "16:9"] as const).map((f) => (
                  <MenuItem
                    key={f}
                    icon={<span style={{ fontSize: 14 }}>{f === "9:16" ? "📱" : f === "1:1" ? "⬛" : "🖥"}</span>}
                    label={f}
                    hint={f === "9:16" ? "Reels / Shorts / TikTok" : f === "1:1" ? "Feed / Carousel" : "YouTube / LinkedIn"}
                    active={previewFormat === f}
                    onClick={() => { setPreviewFormat(f); close(); }}
                  />
                ))}
              </MenuSection>
            )}
          </Popover>

          <ToolbarSep />

          <IconButton
            icon={sfxConfig.muted ? "🔇" : "🔊"}
            label={
              sfxConfig.muted
                ? "SFX"
                : `SFX ${Math.round(sfxConfig.volume * 100)}%`
            }
            showLabel={!sfxConfig.muted && Math.abs(sfxConfig.volume - 1) > 0.01}
            active={!sfxConfig.muted}
            onClick={toggleGlobalSfx}
            title={
              sfxConfig.muted
                ? "Stumm — Klick zum Aktivieren. Master-Lautstärke im SFX-Panel."
                : `Sound-Effekte AN · Master ${Math.round(sfxConfig.volume * 100)}% — Klick zum Stummschalten`
            }
            variant="ghost"
          />

          <IconButton
            icon="🛒"
            label="Produkte"
            showLabel
            onClick={() => setProductsImportOpen(true)}
            title="Produkt-Detail-Szenen aus Allyver-Partner-URL importieren — Mehrfach-Auswahl mit Reihenfolge."
            variant="ghost"
          />

          <IconButton
            icon="✨"
            label="AI"
            showLabel
            onClick={() => setAiOpen(true)}
            title="AI-Reel-Generator: aus einem Satz eine komplette Scene-Struktur erzeugen"
            variant="primary"
          />

          <IconButton
            icon="?"
            active={helpMode}
            onClick={() => setHelpMode((v) => !v)}
            title={helpMode ? "Hilfe-Modus AN — Tooltips sofort. Klick zum Ausschalten." : "Hilfe-Modus aktivieren — Tooltips erscheinen sofort"}
            variant="ghost"
            ariaLabel="Hilfe-Modus"
          />
        </Toolbar>

        <QuickAddBar
          scenes={scenesCatalog}
          onAdd={addScene}
          onAddText={() => addTextOverlay()}
        />

        <div className="composer-preview">
          <ReelPlayerPreview
            ref={playerHandleRef}
            scenes={scenes}
            overlays={overlays}
            sfxEvents={sfxEvents}
            sfxConfig={sfxConfig}
            sfxMuted={sfxConfig.muted}
            format={previewFormat}
            safeZone={safeZoneOn ? { enabled: true, platform: safeZonePlatform } : undefined}
            theme={theme}
          watermark={watermark.enabled ? watermark : undefined}
          editWatermark={selectedSceneWatermark}
          editWatermarkSceneUid={selectedScene?.uid}
          music={music}
          voiceover={voiceover}
          onPlayerReady={handlePlayerReady}
          selectedUid={selectedUid}
          onSelectUid={(uid) => setSelectedUid(uid)}
          onWatermarkChange={(next) => {
            // Drag/Resize im Player wirkt jetzt PRO SZENE: wir committen
            // den Diff (next vs. effective) in `scene.inputs.extras.wm*`.
            // Fallback auf das globale Watermark-Setting nur, wenn (aus
            // welchem Grund auch immer) keine Szene ausgewählt ist.
            if (!selectedScene || !selectedSceneWatermark) {
              setWatermark(next);
              return;
            }
            const cur = selectedSceneWatermark;
            const patch: Parameters<typeof patchSceneWatermark>[1] = {};
            if (next.position !== cur.position) patch.wmPosition = next.position;
            if (next.opacity !== cur.opacity) patch.wmOpacity = next.opacity;
            if (next.size !== cur.size) patch.wmSize = next.size;
            if (next.paddingPx !== cur.paddingPx) patch.wmPaddingPx = next.paddingPx;
            if (next.offsetX !== cur.offsetX) patch.wmOffsetX = next.offsetX;
            if (next.offsetY !== cur.offsetY) patch.wmOffsetY = next.offsetY;
            if (next.xPct !== cur.xPct) patch.wmXPct = next.xPct;
            if (next.yPct !== cur.yPct) patch.wmYPct = next.yPct;
            if (Object.keys(patch).length > 0) {
              patchSceneWatermark(selectedScene.uid, patch);
            }
          }}
            onScenePatch={patchSceneExtras}
            onOverlayPatch={updateOverlay}
            onAddTextOverlay={() => addTextOverlay()}
            onDropElement={(elementId, xPct, yPct) => {
              const entry = findElementById(elementId);
              if (!entry) return;
              // Build a fresh template, then bake in the drop position so the
              // new layer appears exactly where the user released it.
              const tpl = entry.build() as ElementTemplate;
              addOverlayFromTemplate({ ...tpl, xPct, yPct });
            }}
            playheadFrame={playheadFrame}
            locked={locked}
            snap={snap}
            loop={loopOn}
            initialMuted={muted}
            remountKey={remountKey}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onPlayerReset={resetPlayer}
          />
          <Toolbar className="preview-toolbar" ariaLabel="Vorschau-Info">
            <div
              className={`preview-meter meter-${targetClass}`}
              title={`${scenes.length} Scenes · ${totalSec.toFixed(1)}s von ${DEFAULT_TARGET_SEC}s`}
            >
              <strong>{scenes.length}</strong>
              <span className="preview-meter-dot">·</span>
              <span className={`preview-meter-time ${targetClass}`}>{totalSec.toFixed(1)}s</span>
              <span className="preview-meter-target">/ {DEFAULT_TARGET_SEC}s</span>
            </div>

            <ToolbarSpacer />

            {/* Safe-Zone: Ein-Klick-Toggle (Hauptbutton) + kleiner ▾ für die
                Plattform-Auswahl. So kann man die Safezone direkt mit einem
                Knopfdruck an/aus schalten — die Plattform-Wahl bleibt
                versteckt im Submenü. Default ist Instagram Reels für 9:16. */}
            <div className={`safezone-toggle ${safeZoneOn ? "is-on" : ""}`}>
              <IconButton
                icon="📐"
                label={
                  safeZoneOn
                    ? `Safe · ${
                        safeZonePlatform === "reels" ? "Reels"
                        : safeZonePlatform === "tiktok" ? "TikTok"
                        : safeZonePlatform === "shorts" ? "Shorts"
                        : safeZonePlatform === "stories" ? "Stories"
                        : "Generic"
                      }`
                    : "Safe-Zone"
                }
                showLabel
                active={safeZoneOn}
                onClick={() => setSafeZoneOn((s) => !s)}
                title={
                  safeZoneOn
                    ? `Safe-Zone AN (${safeZonePlatform.toUpperCase()}) — Klick zum Ausschalten`
                    : `Safe-Zone aus — Klick zum Einblenden (${safeZonePlatform.toUpperCase()})`
                }
                variant="ghost"
                size="sm"
              />
              <Popover
                placement="bottom-end"
                width={220}
                trigger={({ open, toggle, ref }) => (
                  <button
                    ref={ref}
                    type="button"
                    className={`safezone-platform-btn ${open ? "is-open" : ""}`}
                    onClick={toggle}
                    title="Plattform für Safe-Zone wählen"
                    aria-label="Plattform wählen"
                  >
                    ▾
                  </button>
                )}
              >
                {({ close }) => (
                  <MenuSection title="Plattform">
                    {(["reels", "tiktok", "shorts", "stories", "general"] as const).map((p) => (
                      <MenuItem
                        key={p}
                        icon={p === "reels" ? "📸" : p === "tiktok" ? "🎵" : p === "shorts" ? "▶️" : p === "stories" ? "📖" : "🌐"}
                        label={
                          p === "reels" ? "Instagram Reels"
                          : p === "tiktok" ? "TikTok"
                          : p === "shorts" ? "YouTube Shorts"
                          : p === "stories" ? "Stories"
                          : "Generic (alle)"
                        }
                        active={safeZonePlatform === p}
                        onClick={() => { setSafeZonePlatform(p); setSafeZoneOn(true); close(); }}
                      />
                    ))}
                  </MenuSection>
                )}
              </Popover>
            </div>

            {/* Theme-Umschalter: schaltet das gesamte Reel (alle Szenen) zwischen
                Dark und Light. Wirkt sofort in der Live-Vorschau und wird mit dem
                Reel gespeichert. Light-Palette stammt von www.allyver.com. */}
            <IconButton
              icon={theme === "light" ? "☀️" : "🌙"}
              label={theme === "light" ? "Light" : "Dark"}
              showLabel
              active={theme === "light"}
              onClick={() => { setTheme((t) => (t === "light" ? "dark" : "light")); setDirty(true); }}
              title={
                theme === "light"
                  ? "Light-Theme aktiv (heller Grund) — Klick für Dark"
                  : "Dark-Theme aktiv (dunkler Grund) — Klick für Light"
              }
              variant="ghost"
              size="sm"
            />

            <IconButton
              icon="⇲"
              label="15s"
              showLabel
              onClick={fitTo15}
              disabled={scenes.length === 0}
              title="Alle Clips proportional auf 15s strecken"
              variant="ghost"
              size="sm"
            />

            <IconButton
              icon="↻"
              onClick={resetPlayer}
              title="Player neu mounten — falls Vorschau hängt"
              variant="ghost"
              size="sm"
              ariaLabel="Player zurücksetzen"
            />
          </Toolbar>
        </div>

        <TransportBar
          player={player}
          fps={FPS}
          currentFrame={playheadFrame}
          totalFrames={totalFrames}
          isPlaying={isPlaying}
          speed={speed}
          locked={locked}
          loop={loopOn}
          snap={snap}
          muted={muted}
          onSpeedChange={setSpeed}
          onLockToggle={() => setLocked((v) => !v)}
          onLoopToggle={() => setLoopOn((v) => !v)}
          onSnapToggle={() => setSnap((v) => !v)}
          onMuteToggle={() => setMuted((v) => !v)}
        />

        <Timeline
          scenes={scenes}
          layout={layout}
          sceneIndex={sceneIndex}
          selectedUid={selectedUid}
          totalSec={totalSec}
          targetSec={DEFAULT_TARGET_SEC}
          fps={FPS}
          maxTrack={maxTrack}
          music={music}
          onPatchMusic={patchMusic}
          voiceover={voiceover}
          onPatchVoiceover={patchVoiceover}
          onPatchVoiceoverSegment={patchVoiceoverSegment}
          onRemoveVoiceoverSegment={removeVoiceoverSegment}
          onSplitVoiceoverAtPlayhead={splitVoiceoverAtPlayhead}
          zoom={timelineZoom}
          onZoomChange={setTimelineZoom}
          audioRowScale={audioRowScale}
          onAudioRowScaleChange={setAudioRowScale}
          sfxLineMuted={sfxConfig.muted}
          onToggleSfxLineMute={toggleGlobalSfx}
          watermark={watermark}
          playheadSec={playheadFrame / FPS}
          isPlaying={isPlaying}
          snap={snap}
          format={previewFormat as "9:16" | "1:1" | "16:9"}
          onSuppressEmbeddedSfxBeat={suppressEmbeddedSfxBeat}
          onMuteEmbeddedSceneBundledSfx={muteEmbeddedBundledSceneSfx}
          onSelect={(uid) => setSelectedUid(uid || null)}
          onRemove={removeScene}
          onDuplicate={duplicateScene}
          onSplit={splitSceneAtPlayhead}
          onPatchTime={patchSceneTime}
          onMoveSceneSequence={moveSceneSequence}
          onSeek={onSeekSec}
          onFocusTrack={(t) =>
            setSidebarTab(
              t === "sfx"
                ? "sfx"
                : t === "music"
                  ? "music"
                  : t === "voiceover"
                    ? "voiceover"
                    : "watermark",
            )
          }
          overlays={overlays}
          onPatchOverlayTime={(uid, patch) => updateOverlay(uid, patch)}
          onRemoveOverlay={removeOverlay}
          onDuplicateOverlay={duplicateOverlay}
          onDropElementOnTimeline={(elementId, { fromSec, track }) => {
            const entry = findElementById(elementId);
            if (!entry) return;
            const tpl = entry.build() as ElementTemplate;
            addOverlayFromTemplate({ ...tpl, fromSec, track });
          }}
          sfxEvents={sfxEvents}
          onPatchSfxEventTime={updateSfxEvent}
          onRemoveSfxEvent={removeSfxEvent}
          onDuplicateSfxEvent={duplicateSfxEvent}
          onDropSfxOnTimeline={(sound, { fromSec, durSec, custom }) =>
            addSfxEvent(sound, fromSec, durSec, custom)
          }
        />

        <Toolbar className="composer-render-bar" ariaLabel="Render-Optionen">
          <Popover
            placement="top-start"
            width={220}
            trigger={({ open, toggle, ref }) => (
              <button
                ref={ref}
                type="button"
                className={`render-pick ${open ? "is-open" : ""}`}
                onClick={toggle}
                title="Sprachen für den Export auswählen"
              >
                <span className="render-pick-icon" aria-hidden>🌐</span>
                <span className="render-pick-label">Sprachen</span>
                <span className="render-pick-value">
                  {chosenLocales.length === 0 ? "—" : chosenLocales.length === locales.length ? "Alle" : chosenLocales.map((l) => l.toUpperCase()).join(", ")}
                </span>
                <span className="save-chip-caret" aria-hidden>▾</span>
              </button>
            )}
          >
            <MenuSection title="Sprachen">
              {locales.map((l) => {
                const on = chosenLocales.includes(l);
                return (
                  <MenuItem
                    key={l}
                    icon={on ? "☑" : "☐"}
                    label={l.toUpperCase()}
                    active={on}
                    onClick={() => toggleLocale(l)}
                  />
                );
              })}
            </MenuSection>
          </Popover>

          <Popover
            placement="top-start"
            width={220}
            trigger={({ open, toggle, ref }) => (
              <button
                ref={ref}
                type="button"
                className={`render-pick ${open ? "is-open" : ""}`}
                onClick={toggle}
                title="Formate für den Export auswählen"
              >
                <span className="render-pick-icon" aria-hidden>📐</span>
                <span className="render-pick-label">Formate</span>
                <span className="render-pick-value">
                  {chosenFormats.length === 0 ? "—" : chosenFormats.length === formats.length ? "Alle" : chosenFormats.join(", ")}
                </span>
                <span className="save-chip-caret" aria-hidden>▾</span>
              </button>
            )}
          >
            <MenuSection title="Formate">
              {formats.map((f) => {
                const on = chosenFormats.includes(f);
                return (
                  <MenuItem
                    key={f}
                    icon={on ? "☑" : "☐"}
                    label={f}
                    active={on}
                    onClick={() => toggleFormat(f)}
                  />
                );
              })}
            </MenuSection>
          </Popover>

          <ToolbarSpacer />

          <IconButton
            icon="📋"
            label="In Queue"
            showLabel
            onClick={() => void submit(false)}
            disabled={busy}
            title="Render in die Warteschlange einreihen"
            variant="ghost"
          />
          <IconButton
            icon="🚀"
            label="Sofort rendern"
            showLabel
            onClick={() => void submit(true)}
            disabled={busy}
            title="Render sofort starten — alle ausgewählten Formate × Sprachen"
            variant="primary"
          />
        </Toolbar>

        {err && <div className="error-row">{err}</div>}
      </section>

      {!isMobile && insMode === "docked" && (
        <ColumnResizer side="right" current={dock.state.colRight} onChange={(px) => dock.setColumn("right", px)} />
      )}

      <DockablePanel
        id="inspector"
        title="Inspector"
        icon="🎛"
        mode={insMode}
        float={dock.state.floats.inspector}
        onToggleCollapse={() => dock.cycleCollapse("inspector")}
        onTogglePopup={() => dock.togglePopup("inspector")}
        onFloatChange={(r) => dock.setFloat("inspector", r)}
        popupTitle="Inspector als Popup öffnen"
        collapseTitle="Inspector minimieren"
        className="composer-inspector"
      >
        <div className="inspector-tabs">
          <button
            className={`tab-btn ${sidebarTab === "layers" ? "active" : ""}`}
            onClick={() => setSidebarTab("layers")}
            title="Layers — wie in Photoshop: Reihenfolge, Sichtbarkeit, Sperre"
          >
            🪟 Layers{overlays.length > 0 ? <span className="tab-badge">{overlays.length}</span> : null}
          </button>
          <button
            className={`tab-btn ${sidebarTab === "inspector" ? "active" : ""}`}
            onClick={() => setSidebarTab("inspector")}
          >
            🎛️ Scene
          </button>
          <button
            className={`tab-btn ${sidebarTab === "watermark" ? "active" : ""}`}
            onClick={() => setSidebarTab("watermark")}
          >
            🎯 Logo
          </button>
          <button
            className={`tab-btn ${sidebarTab === "music" ? "active" : ""}`}
            onClick={() => setSidebarTab("music")}
          >
            🎵 Music{music?.enabled ? <span className="tab-badge good">●</span> : null}
          </button>
          <button
            className={`tab-btn ${sidebarTab === "voiceover" ? "active" : ""}`}
            onClick={() => setSidebarTab("voiceover")}
            title="Voiceover — eigene Sprachaufnahme hochladen, schneiden & positionieren"
          >
            🎙️ VO{voiceover?.enabled && voiceover.src ? <span className="tab-badge good">●</span> : null}
          </button>
          <button
            className={`tab-btn ${sidebarTab === "sfx" ? "active" : ""}`}
            onClick={() => setSidebarTab("sfx")}
            title="Sound-Effekte — Galerie + Timeline-Bars"
          >
            🔊 SFX{sfxEvents.length > 0 ? <span className="tab-badge">{sfxEvents.length}</span> : null}
          </button>
          <button
            className={`tab-btn ${sidebarTab === "eleven" ? "active" : ""}`}
            onClick={() => setSidebarTab("eleven")}
            title="Vertonung mit ElevenLabs — Stimme tauschen, TTS, Musik, SFX, Auto-Vertonung"
          >
            🎧 Vertonung
          </button>
          <button
            className={`tab-btn ${sidebarTab === "notes" ? "active" : ""}`}
            onClick={() => setSidebarTab("notes")}
          >
            📝 Notes {notes.length > 0 ? <span className="tab-badge">{notes.length}</span> : null}
          </button>
          <button
            className={`tab-btn ${sidebarTab === "cr" ? "active" : ""}`}
            onClick={() => setSidebarTab("cr")}
          >
            ✅ CR{" "}
            {changeRequests.length > 0 ? (
              <span className={`tab-badge ${openCRs > 0 ? "warn" : "good"}`}>
                {changeRequests.length - openCRs}/{changeRequests.length}
              </span>
            ) : null}
          </button>
        </div>

        <div className="inspector-body">
          {sidebarTab === "layers" && (
            <LayersPanel
              overlays={overlays}
              scenes={scenes}
              scenesCatalog={scenesCatalog}
              format={previewFormat}
              selectedUid={selectedUid}
              selectedUids={selectedUids}
              onSelect={(uid, mode, orderedUids) => {
                applySelection(uid, mode, orderedUids);
                if (uid && mode === "replace") setSidebarTab("inspector");
              }}
              onOverlayPatch={updateOverlay}
              onReorderOverlays={replaceOverlays}
              onDuplicateOverlay={duplicateOverlay}
              onRemoveOverlay={removeOverlay}
              onBulkPatch={bulkPatchOverlays}
              onBulkRemove={bulkRemoveOverlays}
              onBulkDuplicate={bulkDuplicateOverlays}
              onGroup={groupOverlays}
              onUngroup={ungroupOverlays}
              onSeekSec={onSeekSec}
              onReorderScenes={replaceScenes}
              onPatchSceneExtras={patchSceneVisibility}
              onDuplicateScene={duplicateScene}
              onRemoveScene={removeScene}
            />
          )}
          {sidebarTab === "inspector" && (
            isOverlaySelected && selectedOverlay ? (
              <OverlayInspector
                overlay={selectedOverlay}
                onChange={updateOverlay}
                onDelete={removeOverlay}
                onDuplicate={duplicateOverlay}
              />
            ) : isSfxSelected && selectedSfxEvent ? (
              <SfxInspector
                sfxEvent={selectedSfxEvent}
                onChange={(patch) => updateSfxEvent(selectedSfxEvent.uid, patch)}
                onDelete={() => removeSfxEvent(selectedSfxEvent.uid)}
                onDuplicate={() => duplicateSfxEvent(selectedSfxEvent.uid)}
              />
            ) : (
              <Inspector
                scene={selectedScene}
                meta={selectedMeta}
                onChange={(p) => updateScene(p.uid, p)}
                watermark={watermark}
                autoSfxBeatsCount={
                  selectedScene
                    ? sfxEvents.filter((e) => e.sceneUid === selectedScene.uid).length
                    : 0
                }
                onApplyAutoSfx={applyAutoSfxForScene}
                onClearAutoSfx={clearAutoSfxForScene}
              />
            )
          )}
          {sidebarTab === "watermark" && (
            <WatermarkPanel value={watermark} onChange={setWatermark} />
          )}
          {sidebarTab === "music" && (
            <MusicPanel
              value={music}
              onChange={setMusic}
              reelDurationSec={totalSec}
              playheadSec={playheadFrame / FPS}
            />
          )}
          {sidebarTab === "voiceover" && (
            <VoiceoverPanel
              value={voiceover}
              onChange={(next) => setVoiceover(next ? voEnsureSegments(next) : undefined)}
              reelDurationSec={totalSec}
              playheadSec={playheadFrame / FPS}
              onSplitAtPlayhead={splitVoiceoverAtPlayhead}
              segmentCount={voiceover?.segments?.length ?? 0}
              recordPlayPreview={recordPlayPreview}
              onRecordPlayPreviewToggle={setRecordPlayPreview}
              onPlayPreview={playPreviewSilent}
              onPausePreview={pausePreviewSilent}
              onRecordingFinished={handleRecordingFinished}
              onExportVoiceover={handleExportVoiceover}
            />
          )}
          {sidebarTab === "sfx" && (
            <SfxGallery
              playheadSec={playheadFrame / FPS}
              sfxEventCount={sfxEvents.length}
              onAdd={(sound, fromSec, durSec) => addSfxEvent(sound, fromSec, durSec)}
              onAddCustom={(asset, fromSec) => addCustomSfxEvent(asset, fromSec)}
              masterVolume={sfxConfig.volume}
              masterMuted={sfxConfig.muted}
              onChangeMasterVolume={setSfxVolume}
              onToggleMasterMute={toggleGlobalSfx}
            />
          )}
          {sidebarTab === "eleven" && (
            <ElevenLabsPanel
              fps={FPS}
              scenes={scenes}
              layout={layout}
              totalSec={totalSec}
              playheadSec={playheadFrame / FPS}
              locale={chosenLocales[0] ?? locales[0] ?? "de"}
              selectedSceneText={
                selectedScene
                  ? [selectedScene.inputs?.text, selectedScene.inputs?.subtext]
                      .filter((t): t is string => typeof t === "string" && t.trim().length > 0)
                      .join(". ")
                  : ""
              }
              hasVoiceover={!!voiceover?.src || (voiceover?.segments?.length ?? 0) > 0}
              recordPlayPreview={recordPlayPreview}
              onRecordPlayPreviewToggle={setRecordPlayPreview}
              onPlayPreview={playPreviewSilent}
              onPausePreview={pausePreviewSilent}
              onPlaceVoiceover={placeVoiceoverAsset}
              onSetMusic={setMusicFromAsset}
              onAddSfx={(asset, fromSec) => addCustomSfxEvent(asset, fromSec)}
              onApplyAutoScore={applyAutoScore}
              showToast={(kind, message) => {
                if (kind === "error") setErr(message);
              }}
            />
          )}
          {sidebarTab === "notes" && (
            <NotesPanel
              notes={notes}
              disabled={!workspaceId}
              onAdd={addNote}
              onDelete={deleteNote}
            />
          )}
          {sidebarTab === "cr" && (
            <ChangeRequestsPanel
              items={changeRequests}
              disabled={!workspaceId}
              onAdd={addCR}
              onToggle={toggleCR}
              onDelete={deleteCR}
            />
          )}
        </div>
      </DockablePanel>

      {/* Restore-Tabs für mobile/collapsed Panels — schweben am Rand. */}
      {(libMode === "hidden" || (isMobile && libMode === "collapsed")) && (
        <button
          type="button"
          className="dock-restore left"
          onClick={() => dock.setMode("library", "docked")}
          title="Library einblenden"
        >
          📚 Library
        </button>
      )}
      {(insMode === "hidden" || (isMobile && insMode === "collapsed")) && (
        <button
          type="button"
          className="dock-restore right"
          onClick={() => dock.setMode("inspector", "docked")}
          title="Inspector einblenden"
        >
          🎛 Inspector
        </button>
      )}

      {/* Layout-Reset (rechts unten, dezent). */}
      <button
        type="button"
        className="dock-layout-reset"
        onClick={dock.resetLayout}
        title="Layout zurücksetzen — Standard-Spaltenbreiten und Panels wieder andocken."
      >
        ⟲ Layout
      </button>

      <AiPromptModal
        open={aiOpen}
        onClose={() => setAiOpen(false)}
        onResult={(t, s) => {
          const fresh = cloneScenes(s);
          setScenes(fresh);
          setSelectedUid(fresh[0]?.uid ?? null);
          setTitle(t);
        }}
      />

      <PartnerProductsImportModal
        open={productsImportOpen}
        onClose={() => setProductsImportOpen(false)}
        detailMeta={scenesCatalog.find((s) => s.id === "partnerServiceDetail") ?? null}
        onInsert={(newScenes) => insertScenesAfterSelected(newScenes)}
      />
    </div>
  );
};

function autoSaveLabel(
  state: "idle" | "pending" | "saving" | "saved" | "error",
  workspaceId: string | null,
  savedAt: string | null,
): string {
  if (state === "saving") return "Speichert…";
  if (state === "pending") return "● Ungespeicherte Änderungen";
  if (state === "saved") return "✓ Gespeichert";
  if (state === "error") return "⚠ Fehler — klicken zum Wiederholen";
  if (!workspaceId) return "Noch nicht gespeichert";
  if (!savedAt) return "Bereit zum Speichern";
  return `Zuletzt gespeichert ${formatRelative(savedAt)}`;
}

function formatRelative(iso: string): string {
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  if (diff < 60_000) return "gerade eben";
  if (diff < 3600_000) return `vor ${Math.round(diff / 60_000)} min`;
  if (diff < 86_400_000) return `vor ${Math.round(diff / 3600_000)} h`;
  return d.toLocaleDateString("de-DE", { day: "2-digit", month: "short" });
}
